import type { Decor, Rect } from '../world/mapdef';
import { block, ellipse, hash, rect, shade, shadow, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter } from './sprites';
import { LIFE_ANIMATED, LOOKS, drawLife, person } from './streetLife';
import { isNight, line, poly, txt } from './propsHualien';

/**
 * Barcelona (barcelona package): the Sagrada Família with its cranes, Park Güell's gatehouses, dragon
 * and serpentine bench, Eixample facades with iron balconies, La Rambla's flower kiosks, human statues,
 * the Boqueria and the Liceu, the Gothic cathedral, the Bishop's bridge, a sardana ring and castells,
 * the Arc de Triomf and the Cascada, Port Vell, the Barceloneta's sand and the W hotel, and El Prat's
 * arrivals hall with the Aerobús. Street life (crowds, queues, pigeons, benches…) comes from streetLife.
 */

/** Flags set by the content module (the human statue moves when tipped). */
export const bcFx = { statueUntil: 0 };

const TRENCADIS = ['#3a8fd9', '#e2493b', '#ffd34e', '#f4f1ea', '#57c278', '#ff9f43', '#9b5fc0', '#7ad0e0'];
const STONE = '#c9b79a';
const STONE_D = '#9c8a6c';
const IRON = '#2a2c30';

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

const bitmaps = new Map<string, { cv: HTMLCanvasElement; s: number }>();
/** Pre-render once per look into a bitmap and blit (same pattern as `cachedArt` in propsCks). */
function art(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.min(3, Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2));
  let e = bitmaps.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    bitmaps.set(key, e);
  }
  c.drawImage(e.cv, x0, y0, w, h);
}

/** Broken-tile mosaic filling a rect (Gaudí's trencadís). */
function trencadis(c: Ctx, x: number, y: number, w: number, h: number, seed: number, base = '#f4f1ea'): void {
  rect(c, base, x, y, w, h);
  const s = 2.6;
  for (let j = 0; j < h / s; j++) {
    for (let i = 0; i < w / s; i++) {
      const r = hash(i, j, seed);
      if (r < 0.35) continue;
      const col = TRENCADIS[Math.floor(hash(i, j, seed + 1) * TRENCADIS.length)];
      const px = x + i * s + hash(i, j, seed + 2) * 0.8, py = y + j * s + hash(i, j, seed + 3) * 0.8;
      rect(c, col, px, py, Math.min(s - 0.6, x + w - px), Math.min(s - 0.6, y + h - py));
    }
  }
}

/** The senyera: four red stripes on gold, `w` × `h`, with a soft wave. */
function senyera(c: Ctx, x: number, y: number, w: number, h: number, t: number, estelada = false): void {
  const wv = Math.sin(t * 3 + x * 0.1) * 1.2;
  poly(c, '#ffd34e', [x, y, x + w, y + wv, x + w, y + h + wv, x, y + h]);
  for (let k = 0; k < 4; k++) {
    const yy = y + h * (0.12 + k * 0.22);
    poly(c, '#e2493b', [x, yy, x + w, yy + wv, x + w, yy + h * 0.1 + wv, x, yy + h * 0.1]);
  }
  if (estelada) {
    poly(c, '#3a6fd9', [x, y, x + w * 0.42, y + h / 2 + wv * 0.5, x, y + h]);
    ellipse(c, '#ffffff', x + w * 0.14, y + h / 2, 1.2, 1.2);
  }
}

/** Wrought-iron balcony rail across `w` px at `y` (the top of the rail). */
function balcony(c: Ctx, x: number, y: number, w: number): void {
  rect(c, '#3a3a40', x - 1, y + 6, w + 2, 1.4);
  rect(c, 'rgba(0,0,0,0.18)', x - 1, y + 7.4, w + 2, 1.6);
  for (let i = 0; i <= w; i += 1.8) rect(c, IRON, x + i, y, 0.6, 6.5);
  rect(c, IRON, x - 1, y, w + 2, 0.8);
}

/** A tall window with a balcony, shutters (`shut` 0 open, 1 green persiana down) and a lit pane at night. */
function tallWindow(c: Ctx, x: number, y: number, w: number, h: number, night: boolean, shut: number, lit: boolean): void {
  rect(c, 'rgba(0,0,0,0.25)', x - 0.6, y - 0.6, w + 1.2, h + 1.2);
  if (shut === 1) {
    rect(c, '#4f7a4a', x, y, w, h);
    for (let k = 1; k < h; k += 1.6) rect(c, 'rgba(0,0,0,0.2)', x, y + k, w, 0.5);
  } else {
    rect(c, night && lit ? '#ffe3a8' : '#5f7d99', x, y, w, h);
    rect(c, night && lit ? '#fff3cf' : '#9fbed6', x + 0.4, y + 0.4, w * 0.45, h * 0.4);
    rect(c, IRON, x + w / 2 - 0.3, y, 0.6, h);
    if (shut === 2) {
      rect(c, '#8a6a4a', x - 2.2, y, 2, h);
      rect(c, '#8a6a4a', x + w + 0.2, y, 2, h);
    }
  }
  balcony(c, x - 1.5, y + h - 4, w + 3);
}

/** A ground-floor shop: door, display window, a coloured sign band (`kind` picks the trade). */
function shopFront(c: Ctx, x: number, y: number, w: number, kind: number, night: boolean): void {
  const shops: [string, string, string][] = [
    ['FORN', '#c0392b', '#fff3b0'], ['BAR', '#2a5a8a', '#ffffff'], ['FARMÀCIA', '#2a8a5a', '#ffffff'], ['LLIBRERIA', '#6a4a2a', '#ffe8b0'],
    ['FRUITES', '#e68a2a', '#1b1d24'], ['GELATS', '#ff8ac0', '#ffffff'], ['ESTANC', '#8a2a1e', '#ffd34e'], ['CAFÈ', '#3a3a40', '#ffd34e'],
  ];
  const [name, bg, fg] = shops[kind % shops.length];
  block(c, x, y - 15, w, 15, shade(STONE_D, -0.1), undefined, shade(STONE_D, -0.3));
  // Sign band.
  block(c, x + 1, y - 15, w - 2, 4.6, bg, shade(bg, 0.25), shade(bg, -0.3));
  txt(c, name, x + w / 2, y - 12.6, 3.4, fg, 900);
  // Door and window.
  const dw = 6;
  rect(c, '#3a2a20', x + 2, y - 10, dw, 10);
  rect(c, night ? '#ffd68a' : '#6a8aa0', x + 3, y - 9, dw - 2, 6);
  rect(c, night ? '#ffe3a8' : '#8fb0c8', x + dw + 4, y - 10, w - dw - 7, 8);
  rect(c, 'rgba(255,255,255,0.3)', x + dw + 4.6, y - 9.4, (w - dw - 7) * 0.35, 3);
  for (let k = 0; k < 3; k++) ellipse(c, ['#e2493b', '#ffd34e', '#57c278'][(k + kind) % 3], x + dw + 7 + k * 5, y - 4, 1.6, 1.4);
  rect(c, IRON, x + dw + 3.5, y - 10, 0.6, 8);
}

/** Rooftop clutter: parapet, chimneys, a water tank, an antenna, plants on the terrace. */
function rooftop(c: Ctx, x: number, y: number, w: number, seed: number, col: string): void {
  block(c, x - 1, y, w + 2, 4, shade(col, -0.15), shade(col, 0.1), shade(col, -0.4));
  rect(c, 'rgba(255,255,255,0.25)', x, y + 0.6, w, 0.6);
  for (let i = 0; i < w / 18; i++) {
    const r = hash(i, seed, 601);
    const bx = x + 4 + i * 18 + hash(i, seed, 602) * 6;
    if (r < 0.3) block(c, bx, y - 7, 4, 7, '#b36a4a', '#d08a62', '#7a4a30');
    else if (r < 0.5) {
      rect(c, '#55606a', bx + 1.6, y - 12, 0.8, 12);
      for (let k = 0; k < 3; k++) rect(c, '#55606a', bx - 1 + k, y - 11 + k * 2.5, 6 - k * 2, 0.5);
    } else if (r < 0.65) block(c, bx - 1, y - 6, 7, 6, '#c8cdd2', '#eef2f4', '#8a9096');
    else if (r < 0.85) {
      ellipse(c, '#3f8a3a', bx + 2, y - 3, 4, 3);
      ellipse(c, '#6ab860', bx + 1, y - 4.5, 2.2, 1.8);
    }
  }
}

// ---------------------------------------------------------------------------
// Facades (Eixample, Gòtic, Barceloneta) and the big monuments
// ---------------------------------------------------------------------------

const FACADE_COLS = ['#e3c9a3', '#efe3cf', '#e6b8a8', '#cfd2d6', '#b9a98c', '#a89878', '#f4f1ea'];

/** A block facade `w` × `h` seen from the street (variant 0-3 Eixample, 4-5 Gòtic stone, 6 Barceloneta). */
function facade(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 96, h = d.h ?? 96, v = (d.variant ?? 0) % 7, night = isNight();
  const L = d.x - w / 2, top = d.y - h;
  const gothic = v === 4 || v === 5;
  art(c, `bc:fac:${v}:${w}x${h}:${night ? 1 : 0}`, L - 6, top - 16, w + 12, h + 20, (cc) => {
    const col = FACADE_COLS[v];
    // Wall, with a slightly different tone per house (the block is several houses).
    const houses = Math.max(1, Math.round(w / (gothic ? 40 : 56)));
    const hw = w / houses;
    for (let i = 0; i < houses; i++) {
      const hc = shade(col, (hash(i, v, 611) - 0.5) * 0.16);
      rect(cc, hc, L + i * hw, top, hw, h);
      if (gothic) for (let k = 0; k < h / 4; k++) for (let j = 0; j < hw / 7; j++) if (hash(j, k + i * 50, 612) < 0.5) rect(cc, 'rgba(0,0,0,0.07)', L + i * hw + j * 7 + (k % 2) * 3.5, top + k * 4, 6.4, 3.4);
      rect(cc, 'rgba(0,0,0,0.25)', L + i * hw, top, 0.8, h);
    }
    rect(cc, 'rgba(0,0,0,0.3)', L, top, w, 0.8);
    // Floors: a cornice line and a row of windows per 16 px.
    const floorH = gothic ? 20 : 16;
    const floors = Math.floor((h - 16) / floorH);
    for (let f = 0; f < floors; f++) {
      const fy = top + 6 + f * floorH;
      if (!gothic) rect(cc, 'rgba(0,0,0,0.12)', L, fy + floorH - 1.5, w, 1);
      for (let i = 0; i < houses; i++) {
        const n = gothic ? 2 : Math.max(2, Math.round(hw / 16));
        for (let k = 0; k < n; k++) {
          const wx = L + i * hw + (k + 0.5) * (hw / n) - 3;
          const r = hash(k + i * 7, f, 613 + v);
          if (gothic) {
            // Pointed-arch windows, small, with wooden shutters.
            rect(cc, 'rgba(0,0,0,0.3)', wx - 0.6, fy + 3.4, 7.2, 9.6);
            rect(cc, night && r < 0.5 ? '#ffd68a' : '#3f4f60', wx, fy + 4, 6, 9);
            poly(cc, shade(col, -0.25), [wx - 0.6, fy + 4.4, wx + 3, fy + 1.2, wx + 6.6, fy + 4.4]);
            if (r > 0.6) rect(cc, '#6a4a2a', wx - 2.6, fy + 4, 2.2, 9);
            if (r > 0.6) rect(cc, '#6a4a2a', wx + 6.4, fy + 4, 2.2, 9);
            if (r > 0.85) balcony(cc, wx - 1.5, fy + 9, 9);
          } else {
            tallWindow(cc, wx, fy + 2, 6, 11, night, r < 0.25 ? 1 : r < 0.45 ? 2 : 0, hash(k, f + i * 3, 614) < 0.6);
            if (r > 0.9) ellipse(cc, '#4f9a3a', wx + 3, fy + 10, 3, 2);
          }
        }
      }
    }
    // Ground floor.
    const gy = d.y;
    for (let i = 0; i < houses; i++) {
      const gx = L + i * hw;
      if (gothic) {
        block(cc, gx, gy - 16, hw, 16, shade(col, -0.2), undefined, shade(col, -0.45));
        // A big wooden door in a stone arch, and a small shop or a lantern.
        poly(cc, shade(col, -0.4), [gx + hw / 2 - 6, gy, gx + hw / 2 - 6, gy - 10, gx + hw / 2, gy - 15, gx + hw / 2 + 6, gy - 10, gx + hw / 2 + 6, gy]);
        poly(cc, '#4a3020', [gx + hw / 2 - 4.5, gy, gx + hw / 2 - 4.5, gy - 9, gx + hw / 2, gy - 13, gx + hw / 2 + 4.5, gy - 9, gx + hw / 2 + 4.5, gy]);
        rect(cc, 'rgba(0,0,0,0.3)', gx + hw / 2 - 0.3, gy - 12, 0.6, 12);
        if (hash(i, v, 615) < 0.5) {
          rect(cc, IRON, gx + 5, gy - 13, 0.8, 5);
          block(cc, gx + 3.4, gy - 12, 4, 4, night ? '#ffd68a' : '#5a5a60', '#ffffff');
        }
      } else shopFront(cc, gx, gy, hw, i + v * 3 + Math.floor(w / 50), night);
    }
    rooftop(cc, L, top - 4, w, v * 11 + w, col);
    if (v === 6) for (let i = 0; i < houses; i++) rect(cc, '#2a6fb0', L + i * hw + 2, top + 2, hw - 4, 1.2);
    shadow(cc, d.x, d.y + 1, w / 2, 3, 0.25, 20);
  });
  // A flag or two flutters live.
  if (v !== 6 && hash(Math.round(d.x), 1, 616) < 0.7) senyera(c, L + w * 0.3, top + 20, 7, 4.6, t, hash(Math.round(d.x), 2, 617) < 0.3);
}

/** The Sagrada Família: the Nativity facade with four spindle towers, the central tower rising behind. */
function sagrada(c: Ctx, d: Decor): void {
  const w = d.w ?? 288, h = d.h ?? 144, night = isNight();
  const L = d.x - w / 2, base = d.y, top = base - h;
  const artTop = top - 60;
  art(c, `bc:sagrada:${night ? 1 : 0}`, L - 10, artTop - 6, w + 20, h + 70, (cc) => {
    const stone = night ? '#8a7a66' : STONE, dark = night ? '#5a4e40' : STONE_D;
    shadow(cc, d.x, base + 1, w / 2, 4, 0.3, 40);
    // Central tower (Jesus) behind, with its cross.
    poly(cc, dark, [d.x - 14, top + 10, d.x - 8, artTop - 4, d.x + 8, artTop - 4, d.x + 14, top + 10]);
    rect(cc, '#ffd34e', d.x - 1, artTop - 14, 2, 12);
    rect(cc, '#ffd34e', d.x - 4, artTop - 11, 8, 2);
    // The four bell towers: tapering spindles with rows of slit windows and a mosaic finial.
    const towers = [-0.33, -0.12, 0.12, 0.33];
    towers.forEach((k, i) => {
      const tx = d.x + k * w, th = h + (i === 0 || i === 3 ? 30 : 50), tw = 24;
      poly(cc, i % 2 ? stone : shade(stone, -0.08), [tx - tw / 2, base, tx - tw / 2 + 2, top + 30, tx - 5, base - th, tx + 5, base - th, tx + tw / 2 - 2, top + 30, tx + tw / 2, base]);
      rect(cc, 'rgba(0,0,0,0.22)', tx + 4, top + 28, tw / 2 - 5, th - 28 - (top + 28 - base) * 0 - 20);
      for (let y = base - th + 12; y < top + 26; y += 5) {
        const ww = 4 + ((y - top) / (base - th - top)) * 5;
        rect(cc, night ? '#ffd68a' : '#3a3630', tx - ww / 2, y, ww, 2.6);
      }
      for (let y = top + 30; y < base - 6; y += 6) rect(cc, 'rgba(0,0,0,0.25)', tx - tw / 2 + 3, y, tw - 6, 1.2);
      // Finial: a pinnacle of trencadís and a golden ball.
      trencadis(cc, tx - 4, base - th - 8, 8, 8, 620 + i, '#e2493b');
      ellipse(cc, '#ffd34e', tx, base - th - 10, 2.6, 2.6);
      ellipse(cc, '#fff3b0', tx - 0.8, base - th - 10.8, 0.9, 0.9);
    });
    // The nave roof and the side wings: the full width of the block, with buttresses and tall windows.
    rect(cc, shade(stone, -0.2), L, top + 14, w, 32);
    for (let i = 0; i < w / 8; i++) rect(cc, 'rgba(0,0,0,0.12)', L + i * 8, top + 14, 4, 3);
    for (let i = 0; i < w / 12; i++) poly(cc, shade(stone, -0.32), [L + i * 12 + 2, top + 44, L + i * 12 + 6, top + 22, L + i * 12 + 10, top + 44]);
    rect(cc, shade(stone, -0.06), L, top + 44, w, h - 44);
    for (let i = 0; i <= w / 24; i++) {
      const bx = L + i * 24;
      poly(cc, shade(stone, -0.22), [bx - 3, base, bx - 3, top + 50, bx, top + 40, bx + 3, top + 50, bx + 3, base]);
      if (i < w / 24) {
        poly(cc, '#2a2420', [bx + 9, base - 20, bx + 9, top + 70, bx + 12, top + 62, bx + 15, top + 70, bx + 15, base - 20]);
        if (night) rect(cc, 'rgba(255,214,138,0.5)', bx + 10, top + 72, 4, h - 72 - 20);
      }
    }
    for (let y = top + 56; y < base; y += 6) rect(cc, 'rgba(0,0,0,0.08)', L, y, w, 0.8);
    rect(cc, shade(stone, 0.12), L, top + 44, w, 1.2);
    // The facade body: three portals under a steep gable full of sculpture, the rose window.
    const bw = w * 0.5;
    poly(cc, stone, [d.x - bw / 2, base, d.x - bw / 2, top + 36, d.x, top + 2, d.x + bw / 2, top + 36, d.x + bw / 2, base]);
    poly(cc, dark, [d.x - bw / 2 + 2, top + 36, d.x, top + 4, d.x + bw / 2 - 2, top + 36]);
    for (let k = 0; k < 40; k++) {
      const px = d.x + (hash(k, 1, 621) - 0.5) * (bw - 8), py = top + 36 + hash(k, 2, 621) * (h - 52);
      ellipse(cc, hash(k, 3, 621) < 0.5 ? shade(stone, -0.18) : shade(stone, 0.08), px, py, 2.2 + hash(k, 4, 621) * 2, 1.6 + hash(k, 5, 621) * 1.2);
    }
    // Rose window.
    ellipse(cc, dark, d.x, top + 46, 9, 9);
    ellipse(cc, night ? '#ffd68a' : '#5a7fa8', d.x, top + 46, 7.4, 7.4);
    for (let k = 0; k < 8; k++) line(cc, stone, 1, [d.x, top + 46, d.x + Math.cos((k * Math.PI) / 4) * 7.4, top + 46 + Math.sin((k * Math.PI) / 4) * 7.4]);
    ellipse(cc, '#ffd34e', d.x, top + 46, 1.6, 1.6);
    // Portals (Hope · Charity · Faith) with the cypress "tree of life" on the middle one.
    for (const k of [-1, 0, 1]) {
      const px = d.x + k * 36, ph = k === 0 ? 34 : 26, pw = k === 0 ? 20 : 14;
      poly(cc, dark, [px - pw / 2, base, px - pw / 2, base - ph + 8, px, base - ph, px + pw / 2, base - ph + 8, px + pw / 2, base]);
      poly(cc, '#2a2420', [px - pw / 2 + 3, base, px - pw / 2 + 3, base - ph + 10, px, base - ph + 4, px + pw / 2 - 3, base - ph + 10, px + pw / 2 - 3, base]);
      if (night) rect(cc, 'rgba(255,214,138,0.5)', px - pw / 2 + 4, base - 10, pw - 8, 10);
    }
    poly(cc, '#2f8a4a', [d.x - 5, top + 62, d.x, top + 50, d.x + 5, top + 62]);
    poly(cc, '#3fa35a', [d.x - 7, top + 72, d.x, top + 56, d.x + 7, top + 72]);
    for (let k = 0; k < 6; k++) ellipse(cc, '#ffffff', d.x + (hash(k, 7, 622) - 0.5) * 10, top + 58 + hash(k, 8, 622) * 12, 1, 0.8);
    txt(cc, 'SANCTUS', d.x - w * 0.33, top + 20, 2.6, '#ffd34e', 900);
    txt(cc, 'SANCTUS', d.x + w * 0.33, top + 20, 2.6, '#ffd34e', 900);
    // Stone wall of the side wings (the towers' bases), with a lit glow at night.
    if (night) for (let k = 0; k < 4; k++) ellipse(cc, 'rgba(255,200,120,0.18)', d.x + towers[k] * w, top + 40, 22, 60);
  });
}

/** A construction crane beside the basilica (the works have been going on since 1882); the jib swings slowly. */
function crane(c: Ctx, d: Decor, t: number): void {
  const h = d.h ?? 120, x = d.x, y = d.y, v = d.variant ?? 0;
  const col = v ? '#f0c419' : '#e2493b';
  rect(c, col, x - 1.6, y - h, 3.2, h);
  for (let k = 0; k < h / 6; k++) rect(c, 'rgba(0,0,0,0.3)', x - 1.6, y - h + k * 6, 3.2, 0.6);
  const jl = 60 + v * 10, cb = 20;
  const sw = Math.sin(t * 0.25 + v) * 6;
  line(c, col, 2, [x - cb, y - h + 2, x + jl, y - h + 2 + sw]);
  line(c, 'rgba(0,0,0,0.35)', 0.7, [x - cb, y - h + 2, x, y - h - 12, x + jl, y - h + 2 + sw]);
  block(c, x - cb - 2, y - h - 3, 8, 7, '#55606a', '#7a8086');
  block(c, x - 4, y - h - 4, 8, 7, '#55606a', '#7a8086');
  // Hook and a stone block.
  const hx = x + jl * 0.7, hy = y - h + 2 + sw * 0.7;
  rect(c, '#2a2e33', hx, hy, 0.6, 26 + Math.sin(t * 0.5) * 3);
  block(c, hx - 4, hy + 26 + Math.sin(t * 0.5) * 3, 8, 6, STONE_D, STONE);
}

/** The cathedral of Santa Eulàlia: neo-gothic facade with two octagonal towers and a central spire. */
function cathedral(c: Ctx, d: Decor): void {
  const w = d.w ?? 160, h = d.h ?? 112, night = isNight();
  const L = d.x - w / 2, base = d.y, top = base - h;
  art(c, `bc:cathedral:${night ? 1 : 0}`, L - 10, top - 50, w + 20, h + 56, (cc) => {
    const stone = night ? '#7c7468' : '#bdb3a2', dark = night ? '#4e4840' : '#8a8070';
    shadow(cc, d.x, base + 1, w / 2, 4, 0.3, 30);
    rect(cc, stone, L, top, w, h);
    for (let k = 0; k < h / 5; k++) rect(cc, 'rgba(0,0,0,0.08)', L, top + k * 5, w, 0.6);
    // Central gable with the spire.
    poly(cc, stone, [d.x - 34, top, d.x, top - 26, d.x + 34, top]);
    poly(cc, dark, [d.x - 32, top + 1, d.x, top - 22, d.x + 32, top + 1]);
    poly(cc, stone, [d.x - 5, top - 20, d.x, top - 48, d.x + 5, top - 20]);
    rect(cc, '#ffd34e', d.x - 0.6, top - 54, 1.2, 7);
    rect(cc, '#ffd34e', d.x - 2.4, top - 52, 4.8, 1.2);
    // Side towers with pinnacles.
    for (const k of [-1, 1]) {
      const tx = d.x + k * (w / 2 - 14);
      rect(cc, shade(stone, -0.06), tx - 10, top - 20, 20, h + 20);
      poly(cc, dark, [tx - 10, top - 20, tx, top - 36, tx + 10, top - 20]);
      for (let y = top - 10; y < base - 20; y += 12) {
        poly(cc, '#2a2a30', [tx - 3, y + 8, tx - 3, y + 2, tx, y - 1, tx + 3, y + 2, tx + 3, y + 8]);
        if (night) rect(cc, 'rgba(255,214,138,0.6)', tx - 2, y + 3, 4, 4);
      }
      for (const px of [tx - 10, tx + 10]) poly(cc, stone, [px - 2.5, top - 18, px, top - 30, px + 2.5, top - 18]);
    }
    // Pinnacles along the facade, the rose window, the great portal.
    for (let k = -3; k <= 3; k++) if (k) poly(cc, stone, [d.x + k * 10 - 2, top + 1, d.x + k * 10, top - 8 + Math.abs(k) * 2, d.x + k * 10 + 2, top + 1]);
    ellipse(cc, dark, d.x, top + 26, 11, 11);
    ellipse(cc, night ? '#ffd68a' : '#4e6fa0', d.x, top + 26, 9, 9);
    for (let k = 0; k < 12; k++) line(cc, stone, 0.9, [d.x, top + 26, d.x + Math.cos((k * Math.PI) / 6) * 9, top + 26 + Math.sin((k * Math.PI) / 6) * 9]);
    ellipse(cc, stone, d.x, top + 26, 3, 3);
    for (let k = -2; k <= 2; k++) {
      if (k === 0) continue;
      const px = d.x + k * 22;
      poly(cc, '#2a2a30', [px - 4, top + 70, px - 4, top + 50, px, top + 44, px + 4, top + 50, px + 4, top + 70]);
      if (night) rect(cc, 'rgba(255,214,138,0.5)', px - 3, top + 52, 6, 16);
    }
    poly(cc, dark, [d.x - 22, base, d.x - 22, base - 36, d.x, base - 54, d.x + 22, base - 36, d.x + 22, base]);
    poly(cc, shade(dark, -0.2), [d.x - 17, base, d.x - 17, base - 32, d.x, base - 46, d.x + 17, base - 32, d.x + 17, base]);
    poly(cc, '#2a2420', [d.x - 11, base, d.x - 11, base - 26, d.x, base - 38, d.x + 11, base - 26, d.x + 11, base]);
    // Little saints in the arch.
    for (let k = -3; k <= 3; k++) {
      const a = (k / 3) * 1.1;
      ellipse(cc, stone, d.x + Math.sin(a) * 19, base - 30 - Math.cos(a) * 14, 1.6, 2.6);
    }
    // Gargoyles at the corners.
    for (const k of [-1, 1]) {
      const gx = d.x + k * (w / 2 - 30);
      poly(cc, dark, [gx, top + 4, gx + k * 7, top + 2, gx + k * 6, top + 6]);
      ellipse(cc, dark, gx + k * 7, top + 2.5, 2, 1.6);
    }
  });
}

/** The W hotel ("la Vela"): a glass sail on the breakwater. */
function wHotel(c: Ctx, d: Decor): void {
  const w = d.w ?? 96, h = d.h ?? 96, night = isNight();
  const L = d.x - w / 2, base = d.y, top = base - h;
  art(c, `bc:w:${night ? 1 : 0}`, L - 6, top - 6, w + 12, h + 12, (cc) => {
    shadow(cc, d.x, base + 1, w / 2 - 6, 4, 0.3, 40);
    const g = cc.createLinearGradient(L, top, L + w, base);
    g.addColorStop(0, night ? '#2a3a5a' : '#8fc0e0');
    g.addColorStop(0.6, night ? '#1a2a44' : '#5a90c0');
    g.addColorStop(1, night ? '#101a30' : '#3a6a9a');
    cc.fillStyle = g;
    cc.beginPath();
    cc.moveTo(L + 8, base);
    cc.quadraticCurveTo(L + 6, top + 10, L + w * 0.55, top);
    cc.lineTo(L + w * 0.62, top);
    cc.quadraticCurveTo(L + w - 2, top + 30, L + w - 8, base);
    cc.closePath();
    cc.fill();
    cc.strokeStyle = 'rgba(255,255,255,0.35)';
    cc.lineWidth = 0.8;
    cc.stroke();
    for (let y = top + 8; y < base - 4; y += 4) rect(cc, night ? (hash(Math.round(y), 1, 631) < 0.5 ? 'rgba(255,220,160,0.45)' : 'rgba(0,0,0,0.25)') : 'rgba(255,255,255,0.18)', L + 10, y, w - 20, 0.8);
    block(cc, L + 2, base - 12, w - 4, 12, '#d8d4cc', '#f0ece4', '#8a8680');
    txt(cc, 'W', L + w * 0.58, top + 14, 10, '#ffffff', 900, 'rgba(0,0,0,0.4)');
    if (night) ellipse(cc, 'rgba(160,200,255,0.12)', d.x, top + h / 2, w / 2, h / 2);
  });
}

/** The Arc de Triomf: red brick, Mudéjar friezes, the crowns on top. */
function arc(c: Ctx, d: Decor): void {
  const w = d.w ?? 96, x = d.x, y = d.y, night = isNight();
  art(c, `bc:arc:${night ? 1 : 0}`, x - w / 2 - 4, y - 84, w + 8, 88, (cc) => {
    const brick = night ? '#7a3a2a' : '#b5563a', dark = night ? '#4a2418' : '#7a3a26', cream = night ? '#b8a890' : '#e8dcc0';
    shadow(cc, x, y + 1, w / 2, 3, 0.3, 50);
    for (const k of [-1, 1]) {
      const px = x + k * (w / 2 - 12);
      block(cc, px - 12, y - 56, 24, 56, brick, shade(brick, 0.12), dark);
      for (let j = 0; j < 12; j++) for (let i = 0; i < 4; i++) rect(cc, 'rgba(0,0,0,0.1)', px - 11 + i * 6 + (j % 2) * 3, y - 54 + j * 4.5, 5, 3.6);
      block(cc, px - 14, y - 70, 28, 14, brick, shade(brick, 0.12), dark);
      rect(cc, cream, px - 13, y - 66, 26, 2);
      for (let i = 0; i < 6; i++) rect(cc, cream, px - 11 + i * 4.2, y - 60, 2, 2);
      // The crowns (one at each corner, golden).
      for (const q of [-9, 9]) {
        poly(cc, '#ffd34e', [px + q - 4, y - 70, px + q - 4, y - 76, px + q - 2, y - 73, px + q, y - 78, px + q + 2, y - 73, px + q + 4, y - 76, px + q + 4, y - 70]);
      }
    }
    // The arch.
    cc.fillStyle = brick;
    cc.beginPath();
    cc.moveTo(x - w / 2 + 12, y - 56);
    cc.lineTo(x - w / 2 + 12, y - 40);
    cc.arc(x, y - 40, w / 2 - 12, Math.PI, 0);
    cc.lineTo(x + w / 2 - 12, y - 56);
    cc.closePath();
    cc.fill();
    block(cc, x - w / 2 + 10, y - 72, w - 20, 16, brick, shade(brick, 0.12), dark);
    rect(cc, cream, x - w / 2 + 12, y - 68, w - 24, 2.2);
    txt(cc, 'BARCELONA · 1888', x, y - 61, 3.2, cream, 900);
    for (let i = 0; i < (w - 24) / 6; i++) rect(cc, cream, x - w / 2 + 13 + i * 6, y - 72, 3, 1.8);
    cc.strokeStyle = cream;
    cc.lineWidth = 1.4;
    cc.beginPath();
    cc.arc(x, y - 40, w / 2 - 13, Math.PI, 0);
    cc.stroke();
    // Bats (the symbol of Barcelona) in the spandrels.
    for (const k of [-1, 1]) poly(cc, dark, [x + k * 24, y - 58, x + k * 30, y - 62, x + k * 28, y - 58, x + k * 32, y - 54, x + k * 26, y - 55]);
  });
}

/** The Cascada of the Ciutadella: a stone stair with the golden quadriga; water sheets live. */
function cascada(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 80, x = d.x, y = d.y, night = isNight();
  art(c, `bc:cascada:${night ? 1 : 0}`, x - w / 2 - 6, y - 70, w + 12, 76, (cc) => {
    const stone = night ? '#7a7466' : '#c8bea8', dark = night ? '#4a463c' : '#8a8070';
    shadow(cc, x, y + 1, w / 2, 3, 0.3, 30);
    block(cc, x - w / 2, y - 24, w, 24, stone, shade(stone, 0.1), dark);
    block(cc, x - w / 2 + 8, y - 44, w - 16, 20, stone, shade(stone, 0.1), dark);
    block(cc, x - 14, y - 62, 28, 18, stone, shade(stone, 0.1), dark);
    // Arches and columns.
    for (let k = -2; k <= 2; k++) {
      const px = x + k * 14;
      poly(cc, dark, [px - 4, y - 24, px - 4, y - 36, px, y - 41, px + 4, y - 36, px + 4, y - 24]);
      rect(cc, stone, px - 6, y - 44, 2, 20);
    }
    // The quadriga: four golden horses and a chariot.
    for (let k = 0; k < 4; k++) {
      const hx = x - 9 + k * 5;
      ellipse(cc, '#ffd34e', hx, y - 66, 2.6, 1.6);
      ellipse(cc, '#ffd34e', hx + 2, y - 68, 1.2, 1.2);
      rect(cc, '#ffd34e', hx - 2, y - 65, 0.8, 3);
      rect(cc, '#ffd34e', hx + 1.5, y - 65, 0.8, 3);
    }
    ellipse(cc, '#ffd34e', x + 1, y - 71, 2, 2.6);
    rect(cc, '#ffd34e', x + 0.6, y - 69, 0.8, 4);
    // Griffins and a lamp at the ends.
    for (const k of [-1, 1]) {
      ellipse(cc, dark, x + k * (w / 2 - 6), y - 28, 3.6, 2.6);
      ellipse(cc, dark, x + k * (w / 2 - 4), y - 31, 1.8, 1.8);
    }
    // The pool at the foot.
    ellipse(cc, night ? '#2a4a60' : '#5aa8c8', x, y + 2, w / 2 + 2, 4);
  });
  // Water: three sheets falling down the steps, foam at the bottom.
  for (const k of [-1, 0, 1]) {
    const wx = x + k * 20, w0 = k ? 6 : 10;
    for (let i = 0; i < 4; i++) {
      const u = (t * 0.9 + i / 4 + k * 0.1) % 1;
      rect(c, `rgba(220,240,255,${0.55 * (1 - u * 0.5)})`, wx - w0 / 2 + Math.sin(u * 9 + i) * 0.6, y - 42 + u * 40, w0, 3);
    }
    ellipse(c, `rgba(255,255,255,${0.35 + Math.sin(t * 4 + k) * 0.1})`, wx, y - 1, w0 + 3, 1.6);
  }
}

/** Columbus's column at the foot of La Rambla: iron column, stone base with lions, the statue pointing to sea. */
function columbus(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  art(c, `bc:columbus:${night ? 1 : 0}`, x - 32, y - 156, 64, 160, (cc) => {
    const stone = night ? '#7a7466' : '#c0b6a4', dark = night ? '#4a463c' : '#8a8070';
    shadow(cc, x, y + 1, 22, 4, 0.3, 50);
    block(cc, x - 20, y - 14, 40, 14, stone, shade(stone, 0.1), dark);
    block(cc, x - 13, y - 26, 26, 12, stone, shade(stone, 0.1), dark);
    for (const k of [-1, 1]) {
      ellipse(cc, dark, x + k * 17, y - 16, 4.4, 2.6);
      ellipse(cc, dark, x + k * 20, y - 19, 2, 2);
    }
    // The column with its capital and globe.
    rect(cc, '#3a3a40', x - 4, y - 136, 8, 110);
    rect(cc, 'rgba(255,255,255,0.18)', x - 2.6, y - 134, 1.2, 106);
    block(cc, x - 7, y - 142, 14, 7, '#3a3a40', '#5a5a60');
    ellipse(cc, '#3a3a40', x, y - 145, 5, 3.4);
    // The statue, pointing east (to the sea… or to Genoa, say the jokes).
    ellipse(cc, '#4a4a50', x, y - 153, 2.2, 2.2);
    poly(cc, '#4a4a50', [x - 3, y - 143, x - 2, y - 151, x + 2, y - 151, x + 3, y - 143]);
    rect(cc, '#4a4a50', x + 2, y - 151, 6, 1.2);
    // Dedication band.
    txt(cc, 'A COLÓN', x, y - 20, 3, dark, 900);
    if (night) for (const k of [-1, 1]) ellipse(cc, 'rgba(255,220,160,0.2)', x + k * 10, y - 80, 8, 60);
  });
}

// ---------------------------------------------------------------------------
// Park Güell
// ---------------------------------------------------------------------------

/** One of the two gatehouses: wavy roof, trencadís, a tower with a cross (variant 1) or a fly-agaric top. */
function gatehouse(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0, night = isNight();
  art(c, `bc:gate:${v}:${night ? 1 : 0}`, x - 36, y - 92, 72, 98, (cc) => {
    shadow(cc, x, y + 1, 30, 4, 0.3, 30);
    // Stone body, rounded.
    const stone = night ? '#8a7a60' : '#c9a86a';
    cc.fillStyle = stone;
    cc.beginPath();
    cc.roundRect(x - 28, y - 36, 56, 36, 6);
    cc.fill();
    for (let k = 0; k < 30; k++) ellipse(cc, hash(k, v, 641) < 0.5 ? shade(stone, -0.15) : shade(stone, 0.1), x - 26 + hash(k, v, 642) * 52, y - 34 + hash(k, v, 643) * 32, 2.6, 1.8);
    // Windows with trencadís frames and a door.
    for (const wx of [x - 15, x + 15]) {
      rect(cc, '#f4f1ea', wx - 4.6, y - 26, 9.2, 10);
      rect(cc, night ? '#ffd68a' : '#5a7a9a', wx - 3.6, y - 25, 7.2, 8);
      cc.strokeStyle = '#3a8fd9';
      cc.lineWidth = 0.8;
      cc.strokeRect(wx - 4.2, y - 25.6, 8.4, 9.2);
    }
    poly(cc, '#4a3020', [x - 5, y, x - 5, y - 12, x, y - 16, x + 5, y - 12, x + 5, y]);
    // Wavy roof covered in trencadís (cream and brown), a chimney-mushroom.
    cc.save();
    cc.beginPath();
    cc.moveTo(x - 32, y - 36);
    for (let i = 0; i <= 8; i++) cc.lineTo(x - 32 + i * 8, y - 50 - Math.sin(i * 1.3) * 6);
    cc.lineTo(x + 32, y - 36);
    cc.closePath();
    cc.clip();
    trencadis(cc, x - 32, y - 60, 64, 24, 650 + v, '#e8dcc0');
    cc.restore();
    cc.strokeStyle = '#6a4a2a';
    cc.lineWidth = 1;
    cc.beginPath();
    cc.moveTo(x - 32, y - 36);
    for (let i = 0; i <= 8; i++) cc.lineTo(x - 32 + i * 8, y - 50 - Math.sin(i * 1.3) * 6);
    cc.stroke();
    if (v) {
      // The tall checkered tower with the four-armed cross.
      rect(cc, '#3a8fd9', x + 10, y - 86, 10, 36);
      for (let j = 0; j < 9; j++) for (let i = 0; i < 2; i++) if ((i + j) % 2) rect(cc, '#f4f1ea', x + 10 + i * 5, y - 86 + j * 4, 5, 4);
      ellipse(cc, '#f4f1ea', x + 15, y - 86, 5, 4);
      rect(cc, '#e2493b', x + 14.4, y - 92, 1.2, 6);
      rect(cc, '#e2493b', x + 12, y - 90, 6, 1.2);
    } else {
      // Fly-agaric chimney.
      rect(cc, '#f4f1ea', x - 2, y - 62, 4, 10);
      ellipse(cc, '#e2493b', x, y - 62, 7, 4);
      for (let k = 0; k < 5; k++) ellipse(cc, '#ffffff', x - 4 + k * 2, y - 63 + (k % 2), 0.9, 0.7);
    }
  });
}

/** The park gate between the gatehouses: iron palm-leaf gate on stone pillars. */
function guellGate(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 36;
  for (const k of [-1, 1]) block(c, x + k * (w / 2) - 3, y - 22, 6, 22, '#c9a86a', '#e0c08a', '#8a6a40');
  for (let i = -w / 2 + 5; i < w / 2 - 4; i += 4) {
    rect(c, IRON, x + i, y - 18, 0.8, 18);
    poly(c, IRON, [x + i - 2, y - 14, x + i + 0.4, y - 20, x + i + 2.8, y - 14]);
  }
  rect(c, IRON, x - w / 2 + 3, y - 10, w - 6, 0.8);
}

/** The park's rubble wall topped with trencadís medallions ("Park" · "Güell"). */
function guellWall(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  art(c, `bc:gwall:${w}`, L - 2, y - 24, w + 4, 26, (cc) => {
    block(cc, L, y - 16, w, 16, '#b89a6a', '#d0b480', '#7a5a38');
    for (let k = 0; k < w / 5; k++) ellipse(cc, hash(k, 1, 661) < 0.5 ? '#a8885a' : '#c8aa78', L + 2 + k * 5 + hash(k, 2, 661) * 2, y - 12 + hash(k, 3, 661) * 10, 2.4, 1.6);
    for (let i = 0; i < w / 32; i++) {
      const mx = L + 16 + i * 32;
      ellipse(cc, '#f4f1ea', mx, y - 18, 7, 6);
      ellipse(cc, '#3a8fd9', mx, y - 18, 5.6, 4.8);
      txt(cc, i % 2 ? 'GÜELL' : 'PARK', mx, y - 18, 3.2, '#f4f1ea', 900);
    }
  });
}

/** The Hypostyle hall's Doric columns: a back row (variant 0) or just the two end ones (variant 1). */
function columns(c: Ctx, d: Decor): void {
  const w = d.w ?? 192, L = d.x - w / 2, y = d.y, v = d.variant ?? 0;
  const xs: number[] = [];
  if (v) xs.push(L + 8, L + w - 8);
  else for (let i = 0; i < w / 32; i++) xs.push(L + 16 + i * 32);
  for (const x of xs) {
    shadow(c, x, y, 5, 1.8, 0.25, 26);
    poly(c, '#c9b48a', [x - 4, y, x - 3.4, y - 30, x + 3.4, y - 30, x + 4, y]);
    rect(c, 'rgba(255,255,255,0.25)', x - 2.6, y - 28, 1.2, 26);
    rect(c, 'rgba(0,0,0,0.18)', x + 1.6, y - 28, 1.4, 26);
    block(c, x - 6, y - 34, 12, 4, '#d8c49a', '#ece0c0', '#8a7a58');
  }
  if (!v) {
    // The ceiling with its trencadís sun discs.
    block(c, L, y - 40, w, 8, '#d8c49a', '#ece0c0', '#8a7a58');
    for (let i = 0; i < w / 48; i++) {
      const mx = L + 24 + i * 48;
      ellipse(c, '#e2493b', mx, y - 36, 6, 2.6);
      ellipse(c, '#ffd34e', mx, y - 36, 3.6, 1.6);
    }
  }
}

/** The terrace parapet seen from below (variant 0, a wavy trencadís band with a gap for the stairs) or a short side wall. */
function terraceEdge(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y, v = d.variant ?? 0;
  art(c, `bc:tedge:${v}:${w}`, L - 4, y - 18, w + 8, 20, (cc) => {
    if (v) {
      block(cc, L, y - 12, w, 12, '#c9a86a', '#e0c08a', '#8a6a40');
      for (let k = 0; k < w / 6; k++) rect(cc, 'rgba(0,0,0,0.12)', L + k * 6, y - 12 + (k % 2) * 4, 5, 3);
      return;
    }
    for (const [x0, x1] of [[L, d.x - 34], [d.x + 34, L + w]]) {
      block(cc, x0, y - 10, x1 - x0, 10, '#c9a86a', '#e0c08a', '#8a6a40');
      cc.save();
      cc.beginPath();
      cc.moveTo(x0, y - 10);
      for (let x = x0; x <= x1; x += 4) cc.lineTo(x, y - 12 - Math.sin(x / 7) * 3);
      cc.lineTo(x1, y - 10);
      cc.lineTo(x1, y - 4);
      cc.lineTo(x0, y - 4);
      cc.closePath();
      cc.clip();
      trencadis(cc, x0, y - 16, x1 - x0, 12, 670);
      cc.restore();
    }
  });
}

/** The serpentine bench on the terrace (object): a wavy trencadís bench with a few people sitting. */
function guellBench(c: Ctx, r: Rect, t: number): void {
  const x0 = r.x, x1 = r.x + r.w, y = r.y + r.h;
  art(c, 'bc:gbench', x0 - 4, y - 24, r.w + 8, 28, (cc) => {
    shadow(cc, (x0 + x1) / 2, y, r.w / 2, 2.4, 0.25);
    cc.fillStyle = '#c9a86a';
    cc.beginPath();
    cc.moveTo(x0, y);
    for (let x = x0; x <= x1; x += 4) cc.lineTo(x, y - 8 - Math.sin(x / 11) * 3);
    cc.lineTo(x1, y);
    cc.closePath();
    cc.fill();
    cc.save();
    cc.beginPath();
    cc.moveTo(x0, y - 8);
    for (let x = x0; x <= x1; x += 4) cc.lineTo(x, y - 18 - Math.sin(x / 11) * 3);
    cc.lineTo(x1, y - 8);
    for (let x = x1; x >= x0; x -= 4) cc.lineTo(x, y - 8 - Math.sin(x / 11) * 3);
    cc.closePath();
    cc.clip();
    trencadis(cc, x0, y - 22, r.w, 16, 680);
    cc.restore();
    for (let x = x0; x <= x1; x += 4) rect(cc, 'rgba(0,0,0,0.1)', x, y - 8 - Math.sin(x / 11) * 3, 4, 0.8);
  });
  for (const [k, look] of [[0.2, 5], [0.55, 18], [0.8, 27]] as const) person(c, x0 + r.w * k, y - 6, look, 'down', k > 0.5 ? 8 : 0, t, look);
}

/** "El drac": the mosaic salamander on the stairway, water trickling from its mouth. */
function dragon(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  art(c, 'bc:dragon', x - 20, y - 26, 40, 30, (cc) => {
    shadow(cc, x, y, 14, 2.6, 0.25);
    // Body curving up the stairs, head down towards the viewer.
    cc.save();
    cc.beginPath();
    cc.ellipse(x - 2, y - 11, 11, 5.5, -0.5, 0, Math.PI * 2);
    cc.ellipse(x + 8, y - 5, 6, 4.2, 0, 0, Math.PI * 2);
    cc.clip();
    trencadis(cc, x - 20, y - 24, 40, 26, 690, '#ffd34e');
    cc.restore();
    cc.strokeStyle = '#2a6a3a';
    cc.lineWidth = 0.9;
    cc.beginPath();
    cc.ellipse(x - 2, y - 11, 11, 5.5, -0.5, 0, Math.PI * 2);
    cc.stroke();
    cc.beginPath();
    cc.ellipse(x + 8, y - 5, 6, 4.2, 0, 0, Math.PI * 2);
    cc.stroke();
    // Legs, eyes, the tail curling up.
    for (const [lx, ly] of [[-10, -6], [-4, -3], [4, -1], [12, -1]] as const) ellipse(cc, '#3a8fd9', x + lx, y + ly, 2.2, 1.4);
    ellipse(cc, '#ffffff', x + 10, y - 7, 1.4, 1.4);
    ellipse(cc, '#ffffff', x + 6.5, y - 7.5, 1.4, 1.4);
    ellipse(cc, '#1b1d24', x + 10.4, y - 7, 0.6, 0.6);
    ellipse(cc, '#1b1d24', x + 6.9, y - 7.5, 0.6, 0.6);
    line(cc, '#e2493b', 2.2, [x - 12, y - 14, x - 16, y - 20, x - 13, y - 25]);
  });
  // Water from the mouth into the basin.
  for (let i = 0; i < 3; i++) {
    const u = (t * 1.2 + i / 3) % 1;
    ellipse(c, `rgba(200,230,255,${0.6 * (1 - u)})`, x + 9 + u * 2, y - 2 + u * 4, 1, 1.2);
  }
}

/** The park's name board in trencadís letters. */
function guellSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  block(c, x - 22, y - 16, 44, 12, '#c9a86a', '#e0c08a', '#8a6a40');
  trencadis(c, x - 20, y - 14, 40, 8, 700, '#f4f1ea');
  txt(c, 'PARK GÜELL', x, y - 10, 5.4, '#1b1d24', 900, 'rgba(255,255,255,0.5)');
}

/** Iron palm fronds (Gaudí's ironwork) on a low wall. */
function ironwork(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 30;
  block(c, x - w / 2, y - 6, w, 6, '#c9a86a', '#e0c08a', '#8a6a40');
  for (let i = -w / 2 + 4; i < w / 2 - 2; i += 6) {
    line(c, IRON, 0.9, [x + i, y - 6, x + i, y - 18]);
    for (const k of [-1, 1]) line(c, IRON, 0.8, [x + i, y - 14, x + i + k * 3, y - 19], );
    for (const k of [-1, 1]) line(c, IRON, 0.8, [x + i, y - 10, x + i + k * 3.5, y - 14]);
  }
}

/** The lookout at the top of the park: a rail, a telescope, a tourist looking out. */
function lookout(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 40;
  rect(c, IRON, x - w / 2, y - 10, w, 0.8);
  rect(c, IRON, x - w / 2, y - 6, w, 0.8);
  for (let i = -w / 2; i <= w / 2; i += 6) rect(c, IRON, x + i, y - 10, 0.8, 10);
  block(c, x + 10, y - 20, 1.6, 12, '#55606a', '#7a8086');
  block(c, x + 7, y - 24, 8, 4, '#3a3e44', '#6a6e74');
  person(c, x - 6, y - 2, 12, 'up', 8, t);
}

/** Monk parakeets (Barcelona's green parrots), hopping about and squawking off now and then. */
function parrots(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0, w = d.w ?? 40;
  const cyc = (t + v * 7) % 26, flying = cyc > 21, u = flying ? (cyc - 21) / 5 : 0;
  for (let i = 0; i < 3; i++) {
    const px = d.x + (hash(i, v, 711) - 0.5) * w, py = d.y + (hash(i, v, 712) - 0.5) * 8;
    const dir = hash(i, v, 713) < 0.5 ? -1 : 1;
    if (flying) {
      const fx = px + dir * u * 30, fy = py - Math.sin(u * Math.PI) * 36, f = Math.sin(t * 20 + i) * 2.4;
      poly(c, '#4fae4a', [fx - 1, fy, fx - 5, fy - f, fx - 2, fy + 0.6]);
      poly(c, '#4fae4a', [fx + 1, fy, fx + 5, fy - f, fx + 2, fy + 0.6]);
      ellipse(c, '#6fcf5a', fx, fy, 2.2, 1.1);
      continue;
    }
    const hop = Math.abs(Math.sin(t * 3 + i * 2)) > 0.9 ? 1.4 : 0;
    ellipse(c, 'rgba(20,10,6,0.2)', px, py, 2.4, 0.8);
    ellipse(c, '#4fae4a', px, py - 2 - hop, 2.6, 1.8);
    ellipse(c, '#6fcf5a', px + dir * 2, py - 3.6 - hop, 1.3, 1.2);
    ellipse(c, '#c8d0c8', px + dir * 2, py - 3 - hop, 0.9, 0.7);
    rect(c, '#e8a060', px + dir * 3.1, py - 3.7 - hop, 0.9, 0.6);
    line(c, '#3a8a3a', 0.8, [px - dir * 2, py - 2, px - dir * 5, py - 1]);
  }
}

function agave(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  shadow(c, x, y, 8, 2, 0.2);
  for (let k = 0; k < 9; k++) {
    const a = -Math.PI * 0.95 + (k / 8) * Math.PI * 0.9, len = 10 + hash(k, v, 721) * 5;
    poly(c, k % 2 ? '#7fb0a0' : '#6a9a8a', [x, y - 2, x + Math.cos(a) * len, y - 2 + Math.sin(a) * len * 0.7, x + Math.cos(a) * len * 0.5 + 2, y - 1]);
  }
  if (v) rect(c, '#8a9a5a', x - 0.6, y - 30, 1.2, 28);
}

// ---------------------------------------------------------------------------
// Streets: lamps, buses, taxis, scooters, Bicing, kiosks, flags, awnings
// ---------------------------------------------------------------------------

/** Barcelona's grey street lamp with a hanging lantern; glows at night. */
function lamp(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, night = isNight();
  shadow(c, x, y, 4, 1.4, 0.3, 36);
  rect(c, '#4a4e54', x - 1.2, y - 40, 2.4, 40);
  rect(c, 'rgba(255,255,255,0.2)', x - 0.4, y - 38, 0.8, 36);
  block(c, x - 3, y - 4, 6, 4, '#3a3e44', '#5a5e64');
  line(c, '#4a4e54', 1.4, [x, y - 40, x, y - 46, x + 8, y - 46]);
  block(c, x + 5, y - 46, 6, 7, night ? '#fff0c0' : '#e8ecef', '#ffffff', '#8a9096');
  if (night) ellipse(c, 'rgba(255,230,160,0.3)', x + 8, y - 42, 10, 7);
}

/** Pere Falqués's modernist bench-lamp of Passeig de Gràcia: an iron lamp curling over a trencadís bench. */
function falques(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, night = isNight();
  art(c, `bc:falques:${night ? 1 : 0}`, x - 24, y - 60, 48, 64, (cc) => {
    shadow(cc, x, y, 18, 2.6, 0.25, 30);
    block(cc, x - 18, y - 8, 36, 8, '#e8e0cc', '#f4f1ea', '#a8a090');
    trencadis(cc, x - 17, y - 7, 34, 3, 730);
    cc.strokeStyle = IRON;
    cc.lineWidth = 1.8;
    cc.beginPath();
    cc.moveTo(x - 14, y - 8);
    cc.quadraticCurveTo(x - 16, y - 50, x + 2, y - 52);
    cc.quadraticCurveTo(x + 14, y - 52, x + 12, y - 42);
    cc.stroke();
    cc.lineWidth = 1;
    cc.beginPath();
    cc.moveTo(x - 10, y - 30);
    cc.quadraticCurveTo(x - 2, y - 36, x - 6, y - 44);
    cc.stroke();
    block(cc, x + 8, y - 42, 8, 8, night ? '#fff0c0' : '#e8ecef', '#ffffff', '#8a9096');
    if (night) ellipse(cc, 'rgba(255,230,160,0.3)', x + 12, y - 38, 10, 8);
  });
  person(c, x - 6, y - 3, 22, 'down', 1, t, 3);
}

/** Gaudí's six-armed lamp post in Plaça Reial (his first commission, 1879). */
function gaudiLamp(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, night = isNight();
  art(c, `bc:glamp:${night ? 1 : 0}`, x - 20, y - 64, 40, 68, (cc) => {
    shadow(cc, x, y, 6, 2, 0.3, 40);
    block(cc, x - 5, y - 6, 10, 6, '#8a8070', '#b0a890', '#5a5248');
    rect(cc, IRON, x - 1.6, y - 50, 3.2, 44);
    for (let k = 0; k < 3; k++) ellipse(cc, IRON, x, y - 12 - k * 14, 3, 1.6);
    for (let k = 0; k < 6; k++) {
      const a = Math.PI + (k / 5) * Math.PI, ex = x + Math.cos(a) * 15, ey = y - 50 + Math.sin(a) * 6 - 4;
      line(cc, IRON, 1.2, [x, y - 50, ex, ey]);
      block(cc, ex - 2.5, ey - 6, 5, 6, night ? '#fff0c0' : '#e8ecef', '#ffffff', '#8a9096');
      if (night) ellipse(cc, 'rgba(255,230,160,0.28)', ex, ey - 3, 7, 6);
    }
    // The winged helmet of Mercury and the snakes on top.
    ellipse(cc, '#e2493b', x, y - 56, 4, 3);
    poly(cc, '#e2493b', [x - 4, y - 56, x - 8, y - 62, x - 3, y - 58]);
    poly(cc, '#e2493b', [x + 4, y - 56, x + 8, y - 62, x + 3, y - 58]);
  });
}

/** A TMB city bus (red and white), `variant` 1 = the blue Aerobús look is elsewhere; here v0 red, v1 the H-line blue. */
function bus(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 90, night = isNight(), v = d.variant ?? 0;
  const col = v ? '#2a6fb0' : '#e2493b';
  art(c, `bc:bus:${v}:${night ? 1 : 0}`, x - w / 2 - 6, y - 30, w + 12, 34, (cc) => {
    shadow(cc, x, y, w / 2 + 2, 4, 0.32);
    for (const wx of [x - w / 2 + 14, x + w / 2 - 14]) {
      ellipse(cc, '#1b1d24', wx, y - 2.5, 4, 3.8);
      ellipse(cc, '#9aa0a6', wx, y - 2.5, 1.6, 1.6);
    }
    block(cc, x - w / 2, y - 26, w, 23, '#f4f1ea', '#ffffff', '#b8b4ac');
    rect(cc, col, x - w / 2 + 1, y - 12, w - 2, 8);
    rect(cc, col, x - w / 2 + 1, y - 26, w - 2, 3);
    for (let i = 0; i < Math.floor((w - 12) / 12); i++) rect(cc, night ? '#ffe8b0' : '#8fb8d8', x - w / 2 + 6 + i * 12, y - 22, 10, 9);
    rect(cc, 'rgba(255,255,255,0.3)', x - w / 2 + 6, y - 21.4, w - 12, 2);
    block(cc, x - w / 2 + 2, y - 24, 6, 12, '#2a2e33', '#55606a');
    txt(cc, v ? 'H16' : 'TMB', x + w / 2 - 12, y - 8, 3.4, '#ffffff', 900);
    txt(cc, v ? 'Barceloneta' : 'Sagrada Família', x - 6, y - 8, 3, '#ffffff', 800);
  });
}

/** A black-and-yellow Barcelona taxi (`variant` 1 faces the other way). */
function taxi(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, left = (d.variant ?? 0) === 1, night = isNight();
  art(c, `bc:taxi:${left ? 1 : 0}:${night ? 1 : 0}`, x - 20, y - 18, 40, 22, (cc) => {
    shadow(cc, x, y, 16, 3.2, 0.3);
    for (const wx of [x - 10, x + 10]) {
      ellipse(cc, '#1b1d24', wx, y - 2, 3, 3);
      ellipse(cc, '#9aa0a6', wx, y - 2, 1.2, 1.2);
    }
    block(cc, x - 15, y - 9, 30, 7, '#1b1d24', '#3a3e44', '#000000');
    block(cc, x - 9, y - 15, 18, 7, '#ffd34e', '#fff0a0', '#c8a020');
    rect(cc, night ? '#ffe8b0' : '#8fb8d8', x - 7, y - 14, 6, 5);
    rect(cc, night ? '#ffe8b0' : '#8fb8d8', x + 1, y - 14, 6, 5);
    rect(cc, '#ffd34e', x + (left ? -14 : 12), y - 8, 2, 2);
    rect(cc, '#e2493b', x + (left ? 12 : -14), y - 8, 2, 2);
    block(cc, x - 4, y - 18, 8, 3, '#ffffff', undefined, '#8a8680');
    txt(cc, 'TAXI', x, y - 16.6, 2.4, '#1b1d24', 900);
    if (night) rect(cc, 'rgba(255,255,200,0.5)', x - 3.4, y - 17.6, 6.8, 2.2);
  });
}

/** A parked Vespa-style scooter in cream, red or mint. */
function vespa(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, col = ['#f4f1ea', '#e2493b', '#7ad0c0'][(d.variant ?? 0) % 3];
  shadow(c, x, y, 9, 2.2);
  ellipse(c, '#1b1d24', x - 6, y - 2, 2.8, 2.8);
  ellipse(c, '#1b1d24', x + 6, y - 2, 2.8, 2.8);
  poly(c, col, [x - 8, y - 4, x - 5, y - 8, x + 2, y - 8, x + 8, y - 4, x + 6, y - 2, x - 6, y - 2]);
  rect(c, '#3a2a20', x - 1, y - 10, 6, 2.4);
  line(c, '#9aa0a6', 1, [x - 6, y - 8, x - 7, y - 13, x - 9, y - 13]);
  ellipse(c, shade(col, -0.2), x + 3, y - 5, 2.6, 2);
}

/** A Bicing station: red-and-white bikes on a row of docks. */
function bicing(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  art(c, `bc:bicing:${w}`, L - 10, y - 30, w + 20, 34, (cc) => {
    shadow(cc, d.x, y, w / 2 + 2, 2.4);
    block(cc, L, y - 3, w, 3, '#8a9096', '#aab0b6', '#5a6066');
    for (let i = 0; i < Math.floor(w / 10); i++) {
      const bx = L + 5 + i * 10;
      block(cc, bx - 1, y - 9, 2, 7, '#e2493b', '#ff6a5a', '#8a2a1e');
      if (hash(i, w, 741) > 0.7) continue;
      ellipse(cc, '#2a2e33', bx - 3, y - 4, 2.6, 2.6);
      ellipse(cc, '#2a2e33', bx + 3, y - 4, 2.6, 2.6);
      line(cc, '#e2493b', 1.1, [bx - 3, y - 4, bx - 0.5, y - 8.5, bx + 2.5, y - 8.5, bx + 3, y - 4]);
      rect(cc, '#f4f1ea', bx - 1.6, y - 10, 3, 1);
    }
    block(cc, L - 8, y - 26, 7, 26, '#e2493b', '#ff6a5a', '#8a2a1e');
    block(cc, L - 7.4, y - 24, 5.8, 7, '#f4f1ea', '#ffffff');
    txt(cc, 'bicing', L - 4.5, y - 20.6, 2.2, '#e2493b', 900);
  });
}

/** A green newsstand / souvenir kiosk (variant 1 souvenirs, 2 newspapers). */
function kiosk(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 1, night = isNight();
  art(c, `bc:kiosk:${v}:${night ? 1 : 0}`, x - 20, y - 36, 40, 40, (cc) => {
    shadow(cc, x, y, 16, 3, 0.3);
    block(cc, x - 15, y - 24, 30, 24, '#2f6a4a', '#4a8a62', '#1e4a32');
    block(cc, x - 17, y - 30, 34, 7, '#2f6a4a', '#4a8a62', '#1e4a32');
    rect(cc, '#ffd34e', x - 16, y - 27, 32, 1);
    // Goods hanging in the front: postcards, magnets and souvenirs, or the day's papers.
    for (let j = 0; j < 2; j++) for (let i = 0; i < 5; i++) {
      const col = v === 2 ? (i % 2 ? '#f4f1ea' : '#e8e0d0') : ['#3a8fd9', '#e2493b', '#ffd34e', '#57c278', '#ff9f43'][(i + j) % 5];
      rect(cc, col, x - 13 + i * 5.4, y - 21 + j * 8, 4.4, 6);
      if (v === 2) rect(cc, '#1b1d24', x - 12.6 + i * 5.4, y - 20 + j * 8, 3.6, 0.8);
    }
    txt(cc, v === 2 ? 'DIARIS · PREMSA' : 'SOUVENIRS', x, y - 26.6, 3, '#fff3b0', 900);
    if (night) rect(cc, 'rgba(255,230,160,0.25)', x - 15, y - 24, 30, 20);
  });
}

/** A senyera (or an estelada, variant 1; variant 2 both flags on a balcony) hung from a balcony. */
function senyeraDecor(c: Ctx, d: Decor, t: number): void {
  const x = d.x, v = d.variant ?? 0, y = d.y - 26;
  rect(c, IRON, x - 6, y, 12, 0.8);
  senyera(c, x - 5, y + 1, 10, 7, t, v === 1);
  if (v === 2) senyera(c, x + 7, y + 1, 10, 7, t);
}

/** A pharmacy's green cross, blinking at night. */
function pharmacy(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y - 22, night = isNight();
  rect(c, IRON, x - 6, y + 4, 12, 0.8);
  block(c, x - 5, y - 4, 10, 10, '#1b1d24', '#3a3e44');
  const on = !night || Math.sin(t * 4) > -0.3;
  const col = on ? '#4ad070' : '#1f5a30';
  rect(c, col, x - 1.4, y - 3, 2.8, 8);
  rect(c, col, x - 4, y - 0.4, 8, 2.8);
  if (night && on) ellipse(c, 'rgba(74,208,112,0.25)', x, y + 1, 9, 8);
}

/** A striped shop awning over a doorway (`variant` picks the colour), fluttering a little. */
function awning(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y - 12, w = d.w ?? 40, col = ['#e2493b', '#2a6fb0', '#2f8a5a'][(d.variant ?? 0) % 3];
  const fl = Math.sin(t * 2 + x * 0.05) * 0.6 + wind.at(x, y) * 1.5;
  poly(c, 'rgba(0,0,0,0.18)', [x - w / 2, y + 2, x + w / 2, y + 2, x + w / 2 + 2, y + 9, x - w / 2 - 2, y + 9]);
  poly(c, col, [x - w / 2, y - 4, x + w / 2, y - 4, x + w / 2 + 3, y + 3 + fl, x - w / 2 - 3, y + 3 + fl]);
  for (let i = 0; i < w / 6; i++) if (i % 2) poly(c, '#fff6e6', [x - w / 2 + i * 6, y - 4, x - w / 2 + i * 6 + 6, y - 4, x - w / 2 + i * 6 + 6.4, y + 3 + fl, x - w / 2 + i * 6 + 0.4, y + 3 + fl]);
  for (let i = 0; i <= w / 6; i++) ellipse(c, (i % 2 ? '#fff6e6' : col), x - w / 2 - 3 + i * 6 + 3, y + 3.2 + fl, 3, 1.6);
}

/** A bus shelter with a route map and someone waiting. */
function busStop(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 40, night = isNight();
  shadow(c, x, y, w / 2, 2.6, 0.25, 20);
  block(c, x - w / 2, y - 26, w, 3, '#3a3e44', '#5a5e64');
  for (const px of [x - w / 2 + 1, x + w / 2 - 3]) rect(c, '#3a3e44', px, y - 24, 2, 24);
  rect(c, night ? 'rgba(255,230,180,0.35)' : 'rgba(150,200,230,0.35)', x - w / 2 + 3, y - 23, w - 6, 20);
  block(c, x + w / 2 - 12, y - 20, 8, 12, '#f4f1ea', '#ffffff', '#b8b4ac');
  for (let k = 0; k < 4; k++) rect(c, ['#e2493b', '#2a6fb0', '#2f8a5a', '#ffd34e'][k], x + w / 2 - 11, y - 18 + k * 2.6, 6, 1.2);
  block(c, x - w / 2 + 3, y - 10, w - 16, 3, '#8a8680', '#b0aca4');
  person(c, x - 8, y - 2, 9 + (d.variant ?? 0) * 5, 'down', 4, t);
}

/** A hanging shop sign on an iron bracket (`variant` picks the trade). */
function vsign(c: Ctx, d: Decor): void {
  const signs: [string, string, string][] = [['BAR', '#2a5a8a', '#ffffff'], ['HOSTAL', '#8a2a1e', '#ffd34e'], ['FORN DE PA', '#c0392b', '#fff3b0'], ['TAPES', '#2f8a5a', '#ffffff']];
  const [text, bg, fg] = signs[(d.variant ?? 0) % signs.length];
  const x = d.x, y = d.y - 44, night = isNight();
  rect(c, IRON, x - 8, y, 16, 1);
  rect(c, IRON, x - 8, y, 1, -6);
  block(c, x - 10, y + 1, 20, 9, bg, shade(bg, 0.2), shade(bg, -0.3));
  txt(c, text, x, y + 5.6, 3.2, fg, 900);
  if (night) ellipse(c, `rgba(255,230,160,0.2)`, x, y + 5, 12, 7);
}

/** A café terrace: two round tables with umbrellas, people sitting (`variant` picks the umbrella colour). */
function terrace(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, col = ['#e2493b', '#f4f1ea', '#2a6fb0'][(d.variant ?? 0) % 3];
  for (const k of [-1, 1]) {
    const tx = x + k * 10, ty = y - 2;
    shadow(c, tx, ty, 9, 2.4, 0.2, 10);
    rect(c, '#3a3e44', tx - 0.6, ty - 30, 1.2, 30);
    ellipse(c, col, tx, ty - 30, 13, 5);
    ellipse(c, shade(col, 0.25), tx - 3, ty - 31.5, 5, 1.6);
    for (let i = 0; i < 6; i++) rect(c, 'rgba(0,0,0,0.12)', tx - 12 + i * 4.3, ty - 31, 0.6, 3);
    person(c, tx - 7, ty - 2, 3 + k + (d.variant ?? 0) * 4, 'right', 0, t, k + 2);
    person(c, tx + 7, ty - 2, 11 + k + (d.variant ?? 0) * 3, 'left', 0, t, k + 5);
    ellipse(c, '#4a4e54', tx, ty - 8, 5.5, 2.4);
    ellipse(c, '#6a6e74', tx, ty - 8.6, 4.6, 1.8);
    ellipse(c, '#f8e8c0', tx - 1.5, ty - 9.2, 1.2, 0.8);
    ellipse(c, '#e2493b', tx + 1.6, ty - 9.4, 0.9, 1.2);
  }
}

// ---------------------------------------------------------------------------
// La Rambla, the port
// ---------------------------------------------------------------------------

/** The flower kiosk on La Rambla (object): buckets of roses, sunflowers and tulips, a green roof. */
function flowerKiosk(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  art(c, `bc:flowers:${night ? 1 : 0}`, x - 24, y - 40, 48, 44, (cc) => {
    shadow(cc, x, y, 18, 3, 0.3);
    block(cc, x - 14, y - 26, 28, 20, '#2f6a4a', '#4a8a62', '#1e4a32');
    block(cc, x - 19, y - 32, 38, 7, '#2f6a4a', '#4a8a62', '#1e4a32');
    rect(cc, '#ffd34e', x - 18, y - 29, 36, 0.8);
    txt(cc, 'FLORS', x, y - 28.6, 3.6, '#fff3b0', 900);
    // Buckets of flowers in front and on the shelves.
    const cols = ['#e2493b', '#ffd34e', '#ff8ac0', '#f4f1ea', '#9b5fc0', '#ff9f43', '#3a8fd9'];
    for (let i = 0; i < 7; i++) {
      const bx = x - 18 + i * 6, by = y - 2 - (i % 2) * 7;
      block(cc, bx - 2.4, by - 6, 4.8, 6, '#4a4e54', '#6a6e74');
      for (let k = 0; k < 4; k++) {
        rect(cc, '#3f8a3a', bx - 1.5 + k, by - 10 + (k % 2), 0.6, 5);
        ellipse(cc, cols[(i + k) % cols.length], bx - 1.5 + k, by - 11 + (k % 2), 1.4, 1.2);
      }
    }
    for (let i = 0; i < 4; i++) for (let k = 0; k < 5; k++) ellipse(cc, cols[(i * 3 + k) % cols.length], x - 10 + i * 6.5 + (k % 2), y - 22 + k * 1.6, 1.3, 1.1);
    if (night) rect(cc, 'rgba(255,200,220,0.2)', x - 14, y - 26, 28, 20);
  });
  // Petals drift now and then.
  const k = (t * 0.4) % 1;
  ellipse(c, `rgba(255,120,160,${0.6 * (1 - k)})`, x + 6 + k * 8, y - 20 + k * 14, 1.1, 0.8);
}

/** A human statue on the Rambla (object): a bronze-painted "Columbus" that only moves when you tip it. */
function humanStatue(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  const moving = Date.now() < bcFx.statueUntil;
  block(c, x - 7, y - 4, 14, 4, '#8a8680', '#b0aca4', '#5a5650');
  block(c, x - 3, y - 2, 6, 2, '#3a3e44', '#6a6e74');
  const look = { skin: '#b8862a', hair: '#8a6a1a', hairStyle: 'neat' as const, hat: '#8a6a1a', top: '#b8862a', topShade: '#8a6a1a', bottom: '#9a7420', shoes: '#6a5010' };
  const bob = moving ? Math.sin(t * 10) * 1.5 : 0;
  drawCharacter(c, look, x, y - 4 + bob, { facing: moving && Math.sin(t * 3) > 0 ? 'left' : 'down', phase: moving ? (t * 3) % 1 : 0, moving, blink: false, breath: 0 });
  // A bronze hat held out… and the tip jar.
  block(c, x - 12, y - 6, 6, 4, '#3a3e44', '#6a6e74');
  for (let k = 0; k < 3; k++) ellipse(c, '#f5b041', x - 10 + k * 1.6, y - 6.5, 0.9, 0.5);
  if (moving) for (let i = 0; i < 3; i++) ellipse(c, '#fff3b0', x + 8 + i * 3, y - 28 - i * 3 - Math.sin(t * 6 + i) * 2, 1, 1);
}

/** The Canaletes fountain (object): drink and you'll come back to Barcelona. */
function canaletes(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 5, 2, 0.3, 20);
  block(c, x - 4, y - 24, 8, 24, '#3a3e44', '#5a5e64', '#1b1d24');
  block(c, x - 5, y - 28, 10, 4, '#3a3e44', '#5a5e64', '#1b1d24');
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * Math.PI * 2;
    block(c, x + Math.cos(a) * 5 - 1.5, y - 30 + Math.sin(a) * 2, 3, 4, '#ffd34e', '#fff3b0');
  }
  rect(c, '#e8e8e8', x + 4, y - 16, 4, 1);
  for (let i = 0; i < 3; i++) {
    const u = (t * 1.5 + i / 3) % 1;
    ellipse(c, `rgba(200,230,255,${0.7 * (1 - u)})`, x + 8, y - 15 + u * 12, 0.9, 1.3);
  }
  ellipse(c, 'rgba(120,160,200,0.4)', x + 8, y - 2, 4, 1.4);
}

/** A portrait painter on the Rambla with an easel and a sitter. */
function painter(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  person(c, x - 10, y - 2, 25, 'right', 0, t, 2);
  rect(c, '#8a6a4a', x - 3, y - 22, 1, 22);
  rect(c, '#8a6a4a', x + 3, y - 22, 1, 22);
  rect(c, '#8a6a4a', x - 4, y - 16, 8, 0.8);
  block(c, x - 5, y - 24, 10, 10, '#f4f1ea', '#ffffff', '#b8b4ac');
  ellipse(c, '#f3c9a0', x, y - 20, 2.2, 2.6);
  rect(c, '#2a1d16', x - 2.2, y - 23.4, 4.4, 1.6);
  for (let k = 0; k < 4; k++) rect(c, ['#e2493b', '#ffd34e', '#3a8fd9', '#57c278'][k], x + 6, y - 14 + k * 1.8, 3, 1.2);
}

/** The Miró mosaic in the Rambla's pavement: red, yellow, blue and black. */
function miro(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y - 8, w = d.w ?? 48;
  art(c, 'bc:miro', x - w / 2 - 2, y - w / 4 - 2, w + 4, w / 2 + 4, (cc) => {
    ellipse(cc, '#f4f1ea', x, y, w / 2, w / 4);
    ellipse(cc, '#e2493b', x - w * 0.18, y - 1, w * 0.14, w * 0.09);
    ellipse(cc, '#ffd34e', x + w * 0.16, y + 2, w * 0.12, w * 0.08);
    ellipse(cc, '#2a6fb0', x + w * 0.04, y - w * 0.12, w * 0.07, w * 0.05);
    ellipse(cc, '#1b1d24', x - w * 0.02, y + 3, w * 0.05, w * 0.035);
    cc.strokeStyle = '#1b1d24';
    cc.lineWidth = 1.2;
    cc.beginPath();
    cc.ellipse(x, y, w / 2 - 1, w / 4 - 1, 0, 0, Math.PI * 2);
    cc.stroke();
    cc.beginPath();
    cc.moveTo(x - w * 0.3, y + w * 0.1);
    cc.quadraticCurveTo(x, y - w * 0.2, x + w * 0.3, y + w * 0.08);
    cc.stroke();
    for (let k = 0; k < 30; k++) rect(cc, 'rgba(0,0,0,0.08)', x - w / 2 + hash(k, 1, 751) * w, y - w / 4 + hash(k, 2, 751) * (w / 2), 2, 2);
  });
}

/** Barcelona's grey litter bin with its orange band. */
function trashBin(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 4, 1.4);
  block(c, x - 3.5, y - 12, 7, 12, '#6a6e74', '#9aa0a6', '#3a3e44');
  rect(c, '#ff9f43', x - 3, y - 9, 6, 1.4);
  rect(c, '#1b1d24', x - 2.4, y - 11, 4.8, 1.2);
}

/** The quay edge: stone kerb, bollards and a chain, over `w` px. */
function quay(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  block(c, L, y - 4, w, 4, '#a8a49c', '#c8c4ba', '#6a6660');
  for (let i = 0; i < w / 48; i++) {
    const bx = L + 24 + i * 48;
    block(c, bx - 2, y - 10, 4, 7, '#2a2e33', '#55606a');
    line(c, '#4a4e54', 0.8, [bx, y - 9, bx + 24, y - 5, bx + 48, y - 9]);
  }
}

/** A yacht moored in Port Vell, bobbing gently. */
function yacht(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y + Math.sin(t * 1.2 + (d.variant ?? 0)) * 0.8, v = d.variant ?? 0;
  ellipse(c, 'rgba(0,0,0,0.15)', x, y + 2, 20, 3);
  poly(c, '#f4f1ea', [x - 20, y - 4, x + 18, y - 4, x + 22, y - 1, x + 16, y + 2, x - 18, y + 2]);
  rect(c, ['#2a6fb0', '#e2493b', '#1b1d24'][v % 3], x - 19, y + 0.5, 39, 1.2);
  block(c, x - 6, y - 10, 14, 6, '#f4f1ea', '#ffffff', '#b8b4ac');
  rect(c, '#6a8aa0', x - 4, y - 9, 10, 2.6);
  rect(c, '#9aa0a6', x - 1, y - 38, 1, 28);
  poly(c, '#f4f1ea', [x, y - 36, x, y - 12, x + 12, y - 12]);
  line(c, '#9aa0a6', 0.5, [x, y - 37, x - 14, y - 6]);
}

/** A "golondrina" tour boat — the old double-decker harbour boats. */
function golondrina(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y + Math.sin(t * 1.4) * 0.6;
  ellipse(c, 'rgba(0,0,0,0.15)', x, y + 2, 26, 3.4);
  poly(c, '#f4f1ea', [x - 26, y - 5, x + 22, y - 5, x + 27, y - 1, x + 20, y + 2, x - 24, y + 2]);
  rect(c, '#2a6fb0', x - 25, y, 50, 1.4);
  block(c, x - 18, y - 12, 36, 7, '#f4f1ea', '#ffffff', '#b8b4ac');
  for (let i = 0; i < 6; i++) rect(c, '#6a8aa0', x - 16 + i * 6, y - 11, 4, 3);
  block(c, x - 14, y - 18, 28, 3, '#2a6fb0', '#4a90d0');
  for (let i = 0; i < 4; i++) person(c, x - 10 + i * 7, y - 12, 4 + i * 3, 'down', 0, t, i);
  txt(c, 'GOLONDRINAS', x, y - 2, 2.4, '#2a6fb0', 900);
}

/** Wooden deck railing on the Rambla de Mar (variant 1: short, vertical). */
function deckRail(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  rect(c, '#8a6a4a', L, y - 10, w, 1);
  rect(c, '#8a6a4a', L, y - 6, w, 0.8);
  for (let i = 0; i <= w; i += 8) rect(c, '#6a4a2a', L + i, y - 10, 1.2, 10);
}

/** Seagulls circling over the water. */
function gulls(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0, w = d.w ?? 60;
  for (let i = 0; i < 4; i++) {
    const u = ((t * (0.08 + hash(i, v, 761) * 0.06)) + hash(i, v, 762)) % 1;
    const a = u * Math.PI * 2;
    const gx = d.x + Math.cos(a) * (w / 2) * (0.6 + hash(i, v, 763) * 0.4), gy = d.y - 30 - hash(i, v, 764) * 30 + Math.sin(a) * 8;
    const f = Math.sin(t * 9 + i * 2) * 2;
    line(c, '#f4f1ea', 1.1, [gx - 5, gy - f, gx, gy, gx + 5, gy - f]);
    line(c, '#8a8e96', 0.6, [gx - 5, gy - f + 0.6, gx, gy + 0.6, gx + 5, gy - f + 0.6]);
  }
}

/** The Boqueria's iron canopy with the mosaic sign over the Rambla entrance. */
function boqueria(c: Ctx, d: Decor): void {
  const w = d.w ?? 96, h = d.h ?? 60, L = d.x - w / 2, base = d.y, top = base - h, night = isNight();
  art(c, `bc:boqueria:${night ? 1 : 0}`, L - 8, top - 10, w + 16, h + 12, (cc) => {
    // Iron canopy roof on columns, stalls glowing inside.
    block(cc, L, top, w, h - 16, night ? '#5a4a3a' : '#8a7a68', shade('#8a7a68', 0.1), '#4a3a30');
    for (let i = 0; i < w / 14; i++) {
      const sx = L + 4 + i * 14;
      block(cc, sx, top + 10, 11, 14, ['#e2493b', '#ffd34e', '#57c278', '#ff9f43', '#3a8fd9'][i % 5], undefined, '#2a2420');
      for (let k = 0; k < 4; k++) ellipse(cc, ['#ff6a3a', '#ffe08a', '#7ad0e0', '#ff8ac0'][(i + k) % 4], sx + 2 + k * 2.4, top + 14 + (k % 2) * 3, 1.1, 1);
      if (night) rect(cc, 'rgba(255,230,160,0.3)', sx, top + 10, 11, 14);
    }
    for (let i = 0; i <= w / 24; i++) rect(cc, IRON, L + i * 24 - 1, top + 4, 2, h - 20);
    block(cc, L - 4, top - 2, w + 8, 8, '#4a4e54', '#6a6e74', '#2a2e33');
    for (let i = 0; i < (w + 8) / 6; i++) rect(cc, 'rgba(255,255,255,0.15)', L - 4 + i * 6, top - 2, 3, 8);
    // The arch sign: stained-glass mosaic in green, red and gold.
    cc.fillStyle = '#2f6a4a';
    cc.beginPath();
    cc.moveTo(d.x - 34, top + 6);
    cc.arc(d.x, top + 6, 34, Math.PI, 0);
    cc.closePath();
    cc.fill();
    cc.fillStyle = '#e2493b';
    cc.beginPath();
    cc.moveTo(d.x - 28, top + 6);
    cc.arc(d.x, top + 6, 28, Math.PI, 0);
    cc.closePath();
    cc.fill();
    for (let k = 0; k < 24; k++) {
      const a = Math.PI + (k / 23) * Math.PI;
      rect(cc, TRENCADIS[k % TRENCADIS.length], d.x + Math.cos(a) * 31 - 1, top + 6 + Math.sin(a) * 31 - 1, 2.4, 2.4);
    }
    txt(cc, 'MERCAT St. JOSEP', d.x, top - 14, 4.2, '#ffd34e', 900, 'rgba(0,0,0,0.4)');
    txt(cc, 'LA BOQUERIA', d.x, top - 7, 5.6, '#fff3b0', 900, 'rgba(0,0,0,0.4)');
    // The entrance arcade at the foot.
    block(cc, L, base - 16, w, 16, night ? '#6a5a48' : '#a89a88', undefined, '#4a3a30');
    for (let i = 0; i < w / 16; i++) {
      poly(cc, '#2a2420', [L + 3 + i * 16, base, L + 3 + i * 16, base - 10, L + 9 + i * 16, base - 14, L + 15 + i * 16, base - 10, L + 15 + i * 16, base]);
      if (night) rect(cc, 'rgba(255,214,138,0.45)', L + 4 + i * 16, base - 9, 10, 9);
    }
  });
}

/** The Boqueria entrance stall (object): paper cups of fruit juice in every colour, fruit, jamón hanging. */
function boqueriaStall(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  art(c, `bc:bqstall:${night ? 1 : 0}`, x - 36, y - 40, 72, 44, (cc) => {
    shadow(cc, x, y, 32, 3, 0.3);
    block(cc, x - 30, y - 10, 60, 10, '#8a6a4a', '#a8885a', '#5a4a30');
    block(cc, x - 32, y - 34, 64, 6, '#e2493b', '#ff6a5a', '#8a2a1e');
    for (let i = 0; i < 10; i++) rect(cc, i % 2 ? '#fff6e6' : '#e2493b', x - 32 + i * 6.4, y - 34, 6.4, 6);
    txt(cc, 'SUCS · FRUITA · PERNIL', x, y - 37, 3, '#fff3b0', 900, 'rgba(0,0,0,0.35)');
    // Juices in rows, tilted trays.
    const juices = ['#ff9f43', '#ffd34e', '#e2493b', '#ff6aa0', '#57c278', '#9b5fc0', '#7ad0e0', '#ff6a3a'];
    for (let j = 0; j < 3; j++) for (let i = 0; i < 9; i++) {
      const jx = x - 26 + i * 6 + (j % 2) * 2, jy = y - 12 - j * 5;
      block(cc, jx - 2, jy - 6, 4, 6, juices[(i + j * 3) % juices.length], shade(juices[(i + j * 3) % juices.length], 0.3));
      rect(cc, '#1b1d24', jx - 0.3, jy - 9, 0.6, 4);
    }
    // Hams hanging from the canopy.
    for (let i = 0; i < 3; i++) {
      const hx = x + 18 + i * 5;
      rect(cc, '#4a3020', hx, y - 28, 0.8, 4);
      poly(cc, '#a84a3a', [hx - 2, y - 24, hx + 3, y - 24, hx + 2.6, y - 14, hx - 1, y - 12, hx - 3, y - 16]);
      rect(cc, '#f4e0c8', hx - 1.5, y - 22, 2.4, 6);
    }
    // Fruit baskets on the left.
    for (let i = 0; i < 3; i++) {
      const bx = x - 28 + i * 7;
      block(cc, bx - 3, y - 26, 6, 4, '#c8a060', '#e0b878', '#8a6a40');
      for (let k = 0; k < 4; k++) ellipse(cc, ['#e2493b', '#ffd34e', '#57c278'][(i + k) % 3], bx - 2 + k * 1.4, y - 27 - (k % 2), 1.2, 1.1);
    }
    if (night) rect(cc, 'rgba(255,220,160,0.22)', x - 32, y - 34, 64, 24);
  });
  // The vendor, blending juice.
  person(c, x + 8, y - 12, 19, 'down', 0, t, 4);
}

/** The Gran Teatre del Liceu: an opera-house front with arches and posters. */
function liceu(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, h = d.h ?? 70, L = d.x - w / 2, base = d.y, top = base - h, night = isNight();
  art(c, `bc:liceu:${night ? 1 : 0}`, L - 4, top - 6, w + 8, h + 8, (cc) => {
    const stone = night ? '#7a6a58' : '#d8c8a8';
    block(cc, L, top, w, h, stone, shade(stone, 0.1), shade(stone, -0.35));
    for (let k = 0; k < h / 16; k++) rect(cc, 'rgba(0,0,0,0.1)', L, top + 14 + k * 16, w, 1);
    for (let i = 0; i < 4; i++) {
      const ax = L + 8 + i * 16;
      poly(cc, '#2a2420', [ax - 5, base, ax - 5, base - 14, ax, base - 19, ax + 5, base - 14, ax + 5, base]);
      if (night) rect(cc, 'rgba(255,214,138,0.5)', ax - 4, base - 13, 8, 13);
      for (let f = 1; f < 3; f++) {
        rect(cc, night ? '#ffd68a' : '#4e6a8a', ax - 3.5, base - 16 - f * 16, 7, 10);
        balcony(cc, ax - 5, base - 10 - f * 16, 10);
      }
    }
    block(cc, L + 2, top + 4, w - 4, 10, '#8a2a1e', '#b03a2a', '#5a1a10');
    txt(cc, 'GRAN TEATRE DEL LICEU', d.x, top + 9, 3.2, '#ffd34e', 900);
    for (let i = 0; i < 3; i++) block(cc, L + 6 + i * 20, top + 18, 12, 14, ['#2a2f4d', '#6a2a4a', '#2a4a3a'][i], undefined, '#1b1d24');
    for (let i = 0; i < 3; i++) txt(cc, ['CARMEN', 'AIDA', 'TOSCA'][i], L + 12 + i * 20, top + 25, 2.4, '#ffd34e', 900);
  });
}

// ---------------------------------------------------------------------------
// Barri Gòtic
// ---------------------------------------------------------------------------

/** The Generalitat's facade on Plaça Sant Jaume: arched balcony, the flags, the clock. */
function generalitat(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 144, h = d.h ?? 32, L = d.x - w / 2, base = d.y, top = base - h, night = isNight();
  art(c, `bc:gencat:${night ? 1 : 0}`, L - 4, top - 10, w + 8, h + 12, (cc) => {
    const stone = night ? '#7a7466' : '#c8bea8';
    block(cc, L, top, w, h, stone, shade(stone, 0.1), shade(stone, -0.35));
    for (let k = 0; k < h / 4; k++) for (let j = 0; j < w / 8; j++) if (hash(j, k, 771) < 0.5) rect(cc, 'rgba(0,0,0,0.06)', L + j * 8 + (k % 2) * 4, top + k * 4, 7.4, 3.4);
    for (let i = 0; i < w / 20; i++) {
      const ax = L + 10 + i * 20;
      poly(cc, '#2a2420', [ax - 5, base, ax - 5, base - 10, ax, base - 14, ax + 5, base - 10, ax + 5, base]);
      if (night) rect(cc, 'rgba(255,214,138,0.5)', ax - 4, base - 9, 8, 9);
    }
    balcony(cc, L + 6, top + 4, w - 12);
    block(cc, d.x - 6, top - 8, 12, 8, stone, shade(stone, 0.1), shade(stone, -0.35));
    ellipse(cc, '#f4f1ea', d.x, top - 4, 3.6, 3.6);
    line(cc, '#1b1d24', 0.6, [d.x, top - 4, d.x, top - 7]);
    line(cc, '#1b1d24', 0.6, [d.x, top - 4, d.x + 2, top - 4]);
    txt(cc, 'GENERALITAT DE CATALUNYA', d.x, top + 12, 3, shade(stone, -0.5), 900);
  });
  senyera(c, d.x - 30, top + 2, 10, 7, t);
  senyera(c, d.x + 20, top + 2, 10, 7, t);
}

/** The Bishop's bridge (Pont del Bisbe) over Carrer del Bisbe: a neo-gothic footbridge with a skull underneath. */
function bishopBridge(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y - 36, w = d.w ?? 32, night = isNight();
  art(c, `bc:bisbe:${night ? 1 : 0}`, x - w / 2 - 6, y - 26, w + 12, 32, (cc) => {
    const stone = night ? '#7a7466' : '#c0b6a4';
    cc.fillStyle = stone;
    cc.beginPath();
    cc.moveTo(x - w / 2 - 4, y + 4);
    cc.lineTo(x - w / 2 - 4, y - 14);
    cc.lineTo(x + w / 2 + 4, y - 14);
    cc.lineTo(x + w / 2 + 4, y + 4);
    cc.lineTo(x + w / 2, y + 4);
    cc.quadraticCurveTo(x, y - 8, x - w / 2, y + 4);
    cc.closePath();
    cc.fill();
    // Tracery windows and pinnacles.
    for (let k = -1; k <= 1; k++) poly(cc, '#2a2420', [x + k * 9 - 2.5, y - 4, x + k * 9 - 2.5, y - 10, x + k * 9, y - 12.5, x + k * 9 + 2.5, y - 10, x + k * 9 + 2.5, y - 4]);
    for (let k = -2; k <= 2; k++) poly(cc, stone, [x + k * 7 - 1.4, y - 14, x + k * 7, y - 20, x + k * 7 + 1.4, y - 14]);
    rect(cc, shade(stone, -0.3), x - w / 2 - 4, y - 14.6, w + 8, 1);
    // The skull with the dagger (the legend).
    ellipse(cc, '#e8e0d0', x, y + 1, 2, 1.8);
    rect(cc, '#2a2420', x - 1, y + 0.5, 0.7, 0.7);
    rect(cc, '#2a2420', x + 0.4, y + 0.5, 0.7, 0.7);
    rect(cc, '#9aa0a6', x - 0.3, y - 3, 0.6, 3);
  });
}

/** A sardana ring: dancers holding hands high, stepping in and out to the cobla. */
function sardana(c: Ctx, d: Decor, t: number): void {
  const n = 8, rx = (d.w ?? 70) / 2, ry = rx * 0.45;
  const step = Math.sin(t * 2.4) * 2;
  const ppl: [number, number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + t * 0.15;
    ppl.push([d.x + Math.cos(a) * (rx + step), d.y + Math.sin(a) * (ry + step * 0.4), i]);
  }
  ppl.sort((a, b) => a[1] - b[1]);
  for (const [x, y, i] of ppl) {
    const facing = y < d.y ? 'down' : 'up';
    const lk = LOOKS[(i * 5 + 3) % 32];
    const hop = Math.max(0, Math.sin(t * 4.8 + i)) * 1.2;
    drawCharacter(c, lk, x, y - hop, { facing, phase: (t * 1.2 + i * 0.3) % 1, moving: true, blink: false, breath: 0 });
    // Raised, joined hands.
    const j = ppl.find((p) => p[2] === (i + 1) % n);
    if (j) line(c, lk.skin, 1.2, [x, y - 24 - hop, (x + j[0]) / 2, y - 27, j[0], j[1] - 24]);
  }
  // The cobla's drum and flabiol player at the side.
  person(c, d.x + rx + 12, d.y + 2, 7, 'left', 0, t);
  block(c, d.x + rx + 18, d.y - 8, 5, 5, '#8a2a1e', '#c04030');
}

/** The cloister's thirteen white geese (one for each year of Saint Eulàlia's life). */
function geese(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 30;
  for (let i = 0; i < 4; i++) {
    const gx = d.x + (hash(i, 1, 781) - 0.5) * w + Math.sin(t * 0.5 + i) * 2, gy = d.y + (hash(i, 2, 781) - 0.5) * 8;
    const dir = hash(i, 3, 781) < 0.5 ? -1 : 1;
    ellipse(c, 'rgba(20,10,6,0.2)', gx, gy, 3, 0.9);
    ellipse(c, '#f8f6f0', gx, gy - 2.6, 3.2, 2.2);
    rect(c, '#f8f6f0', gx + dir * 2.4, gy - 7, 1.2, 4.6);
    ellipse(c, '#f8f6f0', gx + dir * 2.8, gy - 7.4, 1.6, 1.3);
    rect(c, '#ff9f43', gx + dir * 3.6, gy - 7.4, 1.6, 0.8);
    rect(c, '#ff9f43', gx - 0.6, gy - 1, 0.6, 1.2);
  }
}

/** The castell (object): a human tower — pinya, three levels, and the enxaneta on top raising a hand. */
function castell(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  const look = (i: number) => ({ ...LOOKS[i % 32], top: '#2a6fb0', topShade: '#1f5590', apron: undefined, dress: false });
  const sway = Math.sin(t * 1.3) * 1.2;
  // Pinya: a ring of people pressing in at the base.
  const base: [number, number, number][] = [];
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    base.push([x + Math.cos(a) * 15, y + Math.sin(a) * 6 - 2, i]);
  }
  base.sort((a, b) => a[1] - b[1]);
  for (const [bx, by, i] of base) drawCharacter(c, look(i + 4), bx, by, { facing: by < y - 2 ? 'down' : 'up', phase: 0, moving: false, blink: false, breath: (t * 0.5 + i) % 1 });
  // Levels of three, two, one, then the enxaneta.
  const levels: [number, number][][] = [[[-7, 0], [0, 0], [7, 0]], [[-4, 0], [4, 0]], [[0, 0]]];
  levels.forEach((lv, k) => {
    for (const [dx] of lv) drawCharacter(c, look(k * 3 + lv.length), x + dx + sway * (k + 1) * 0.4, y - 10 - k * 19, { facing: 'down', phase: 0, moving: false, blink: false, breath: (t * 0.6 + k) % 1 });
  });
  const kid = { ...LOOKS[4], small: true, top: '#2a6fb0', topShade: '#1f5590', hat: '#1b1d24', hairStyle: 'cap' as const };
  const ky = y - 10 - 3 * 19 + 6;
  drawCharacter(c, kid, x + sway * 1.8, ky, { facing: 'down', phase: 0, moving: false, blink: false, breath: 0 });
  // The "aleta": the enxaneta's raised hand.
  rect(c, kid.skin, x + sway * 1.8 + 4, ky - 22 + Math.sin(t * 3) * 0.6, 1.4, 5);
  // The sashes (faixa) and the castellers' shirts read as one colour: a banner on a pole.
  rect(c, '#8a6a4a', x + 22, y - 44, 1, 44);
  senyera(c, x + 23, y - 43, 8, 6, t);
}

/** A crowd standing in an arc, watching the castell. */
function castellCrowd(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 90;
  for (let i = 0; i < 9; i++) {
    const a = Math.PI * 0.15 + (i / 8) * Math.PI * 0.7;
    const px = d.x + Math.cos(a) * (w / 2), py = d.y + Math.sin(a) * 14;
    person(c, px, py, i * 3 + 1, 'up', i % 4 === 0 ? 4 : i % 3 === 0 ? 8 : 0, t, i);
  }
}

/** The churros stand (object): a granja cart with a copper chocolate pot, churros in paper cones, steam. */
function xurreria(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  art(c, `bc:xurros:${night ? 1 : 0}`, x - 28, y - 42, 56, 46, (cc) => {
    shadow(cc, x, y, 24, 3, 0.3);
    block(cc, x - 22, y - 12, 44, 12, '#8a2a1e', '#b03a2a', '#5a1a10');
    rect(cc, '#ffd34e', x - 21, y - 11, 42, 1);
    for (const px of [x - 20, x + 18]) rect(cc, '#3a2a20', px, y - 36, 2, 24);
    block(cc, x - 24, y - 40, 48, 6, '#ffd34e', '#fff0a0', '#c8a020');
    for (let i = 0; i < 8; i++) if (i % 2) rect(cc, '#8a2a1e', x - 24 + i * 6, y - 40, 6, 6);
    txt(cc, 'XURRERIA · XOCOLATA', x, y - 37, 3, '#1b1d24', 900);
    // Copper pot, churros cones, sugar.
    block(cc, x - 16, y - 22, 12, 10, '#b86a3a', '#e0905a', '#7a4020');
    ellipse(cc, '#5a3a20', x - 10, y - 22, 6, 1.8);
    for (let i = 0; i < 3; i++) {
      const cx = x + 2 + i * 7;
      poly(cc, '#f4f1ea', [cx - 3, y - 14, cx + 3, y - 14, cx + 1, y - 24, cx - 1, y - 24]);
      for (let k = 0; k < 3; k++) rect(cc, '#d9a040', cx - 2 + k * 1.6, y - 28 + (k % 2) * 2, 1.2, 10);
    }
    rect(cc, '#f4f1ea', x - 1, y - 15, 20, 0.8);
    if (night) rect(cc, 'rgba(255,214,138,0.25)', x - 22, y - 34, 44, 22);
  });
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.6 + i / 3) % 1;
    ellipse(c, `rgba(255,255,255,${0.35 * (1 - k)})`, x - 10 + Math.sin(t * 2 + i * 2) * 2, y - 24 - k * 12, 1.5 + k * 2, 1.2 + k * 1.5);
  }
  person(c, x + 10, y - 10, 30, 'down', 0, t, 7);
}

// ---------------------------------------------------------------------------
// Ciutadella, the beach, the airport
// ---------------------------------------------------------------------------

/** The petanca court: boules on the gravel, the little cochonnet, a player crouching to measure. */
function petancaCourt(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 112, L = d.x - w / 2, y = d.y;
  for (let i = 0; i < 7; i++) {
    const bx = L + 12 + hash(i, 1, 791) * (w - 24), by = y - 6 - hash(i, 2, 791) * 30;
    ellipse(c, 'rgba(20,10,6,0.25)', bx, by + 1.6, 2.2, 0.8);
    ellipse(c, '#8a9096', bx, by, 2.2, 2.1);
    ellipse(c, '#c8cdd2', bx - 0.6, by - 0.7, 0.8, 0.7);
    if (i % 2) line(c, '#5a6066', 0.4, [bx - 1.4, by, bx + 1.4, by]);
  }
  ellipse(c, '#e2493b', d.x + 6, y - 22, 1, 1);
  rect(c, '#8a6a4a', L, y - 1, w, 1.2);
  rect(c, '#8a6a4a', L, y - 40, w, 1.2);
  person(c, L + 14, y - 30, 3, 'down', 0, t, 1);
  person(c, d.x + 20, y - 4, 11, 'up', 0, t, 2);
}

/** The petanca box (object): a wooden crate of boules and the scoreboard. */
function petancaBox(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 10, 2);
  block(c, x - 9, y - 7, 18, 7, '#8a6a4a', '#a8885a', '#5a4a30');
  for (let k = 0; k < 4; k++) ellipse(c, '#9aa0a6', x - 6 + k * 4, y - 7.5, 1.8, 1.6);
  block(c, x - 7, y - 20, 14, 10, '#1b1d24', '#3a3e44');
  txt(c, 'PETANCA', x, y - 17, 2.4, '#ffd34e', 900);
  txt(c, '7 · 5', x, y - 13, 3, '#ffffff', 900);
}

/** Rowing boats on the Ciutadella pond. */
function boats(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 60;
  for (let i = 0; i < 2; i++) {
    const bx = d.x + (i - 0.5) * w * 0.6 + Math.sin(t * 0.3 + i) * 4, by = d.y + Math.sin(t * 1.5 + i) * 0.6 + i * 8;
    ellipse(c, 'rgba(0,0,0,0.15)', bx, by + 1.5, 12, 2.4);
    poly(c, '#2a6fb0', [bx - 12, by - 3, bx + 12, by - 3, bx + 9, by + 1.5, bx - 9, by + 1.5]);
    rect(c, '#8a6a4a', bx - 10, by - 2.5, 20, 1);
    person(c, bx - 2, by - 2, 6 + i * 9, 'right', 0, t, i);
    line(c, '#8a6a4a', 0.8, [bx + 2, by - 6, bx + 10 + Math.sin(t * 2 + i) * 3, by + 2]);
  }
}

/** The mammoth statue in the Ciutadella (1907, and kids still climb on it). */
function mammoth(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 14, 3, 0.3, 20);
  ellipse(c, '#7a6a58', x, y - 10, 13, 8);
  ellipse(c, '#8a7a68', x - 2, y - 13, 7, 5);
  for (const lx of [-9, -3, 4, 9]) rect(c, '#7a6a58', x + lx - 1.5, y - 7, 3, 7);
  ellipse(c, '#7a6a58', x + 13, y - 12, 5, 4.4);
  line(c, '#7a6a58', 2.4, [x + 16, y - 10, x + 19, y - 4, x + 15, y]);
  line(c, '#e8e0d0', 1.2, [x + 15, y - 9, x + 21, y - 8, x + 22, y - 12]);
  ellipse(c, '#1b1d24', x + 14.5, y - 13, 0.7, 0.7);
}

/** A picnic blanket with two people and a basket. */
function picnic(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const col = v ? '#e2493b' : '#2a6fb0';
  poly(c, col, [x - 16, y - 2, x + 16, y - 2, x + 20, y + 4, x - 20, y + 4]);
  for (let i = 0; i < 6; i++) poly(c, 'rgba(255,255,255,0.35)', [x - 14 + i * 5.5, y - 2, x - 11 + i * 5.5, y - 2, x - 9.5 + i * 5.5, y + 4, x - 13 + i * 5.5, y + 4]);
  person(c, x - 8, y + 2, 2 + v * 7, 'right', 0, t, 1);
  person(c, x + 9, y + 2, 20 + v * 3, 'left', 0, t, 2);
  block(c, x - 2, y - 4, 6, 4, '#c8a060', '#e0b878', '#8a6a40');
  ellipse(c, '#e2493b', x + 1, y - 5, 1.2, 1.2);
}

/** A low promenade kerb with steps down to the sand, every so often. */
function promRail(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  for (let i = 0; i < w / 64; i++) {
    const sx = L + i * 64;
    block(c, sx, y - 3, 48, 3, '#d8d4cc', '#f0ece4', '#a8a49c');
    for (let k = 0; k < 3; k++) rect(c, 'rgba(0,0,0,0.12)', sx + 48, y - 3 + k, 16, 0.6);
  }
}

/** A beach shower post. */
function shower(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 3, 1.2, 0.25, 20);
  rect(c, '#9aa0a6', x - 1.2, y - 24, 2.4, 24);
  rect(c, '#9aa0a6', x - 1.2, y - 24, 6, 1.6);
  ellipse(c, '#c8cdd2', x + 5, y - 22, 2.4, 1);
  for (let i = 0; i < 3; i++) {
    const u = (t * 1.6 + i / 3) % 1;
    rect(c, `rgba(200,230,255,${0.5 * (1 - u)})`, x + 3 + i * 1.6, y - 21 + u * 18, 0.6, 2);
  }
  ellipse(c, 'rgba(120,160,200,0.35)', x + 5, y - 1, 5, 1.6);
}

/** Towels, parasols and people lying in the sun (`variant` picks colours and poses). */
function sunbathers(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0, w = d.w ?? 60;
  const cols = [['#e2493b', '#ffd34e'], ['#2a6fb0', '#f4f1ea'], ['#57c278', '#ff9f43'], ['#ff8ac0', '#7ad0e0']];
  for (let i = 0; i < 2; i++) {
    const tx = x + (i - 0.5) * w * 0.5, ty = y + i * 5;
    const [a, b] = cols[(v + i) % 4];
    poly(c, a, [tx - 11, ty - 2, tx + 11, ty - 2, tx + 12, ty + 3, tx - 12, ty + 3]);
    for (let k = 0; k < 4; k++) rect(c, b, tx - 10 + k * 5.5, ty - 2, 2.4, 5);
    // Someone lying down: head, body, legs.
    const lk = LOOKS[(v * 5 + i * 11) % 32];
    ellipse(c, lk.skin, tx - 6, ty, 2.2, 2);
    rect(c, lk.hair, tx - 8.4, ty - 2.2, 2.4, 3);
    rect(c, lk.top, tx - 3.5, ty - 1.6, 7, 3.4);
    rect(c, lk.skin, tx + 3.5, ty - 1, 7, 2.2);
  }
  // A parasol.
  const px = x + w * 0.3, py = y - 6;
  rect(c, '#3a3e44', px - 0.5, py - 26, 1, 26);
  const [pa, pb] = cols[(v + 2) % 4];
  ellipse(c, pa, px, py - 26, 14, 5.5);
  for (let k = 0; k < 4; k++) if (k % 2) poly(c, pb, [px, py - 26, px - 14 + k * 7, py - 26 + Math.sin(k) * 2, px - 7 + k * 7, py - 24]);
  ellipse(c, 'rgba(20,10,6,0.18)', px + 4, py + 3, 12, 3.4);
  // A cooler and a ball.
  block(c, x - w * 0.4, y - 5, 6, 4, '#2a6fb0', '#4a90d0', '#1f5590');
  const bb = Math.abs(Math.sin(t * 2.4 + v)) * 6;
  ellipse(c, '#e2493b', x + w * 0.42, y - 3 - bb, 2.4, 2.4);
  ellipse(c, '#f4f1ea', x + w * 0.42 - 0.6, y - 3.6 - bb, 1, 1);
}

function sandcastle(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  block(c, x - 7, y - 6, 14, 6, '#d8c08a', '#e8d4a0', '#a89060');
  block(c, x - 5, y - 12, 4, 6, '#d8c08a', '#e8d4a0', '#a89060');
  block(c, x + 1, y - 12, 4, 6, '#d8c08a', '#e8d4a0', '#a89060');
  rect(c, '#a89060', x - 1, y - 5, 2, 5);
  rect(c, '#3a3e44', x - 3, y - 17, 0.6, 5);
  poly(c, '#e2493b', [x - 2.4, y - 17, x + 1, y - 15.8, x - 2.4, y - 14.6]);
}

/** Surf along the shore: foam lines that run in and draw back. */
function surf(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  for (let k = 0; k < 3; k++) {
    const u = ((t * 0.35 + k / 3) % 1);
    const reach = Math.sin(u * Math.PI) * 7;
    c.strokeStyle = `rgba(255,255,255,${0.75 * (1 - u * 0.6)})`;
    c.lineWidth = 1.4 - k * 0.3;
    c.beginPath();
    for (let x = L; x <= L + w; x += 4) {
      const yy = y - reach + Math.sin(x / 11 + t * 0.6 + k) * 1.5;
      if (x === L) c.moveTo(x, yy);
      else c.lineTo(x, yy);
    }
    c.stroke();
  }
}

/** Skaters and cyclists sliding along the promenade (`variant` seeds direction). */
function skaters(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 160, v = d.variant ?? 0;
  for (let i = 0; i < 3; i++) {
    const speed = 16 + hash(i, v, 801) * 10;
    const u = ((t * speed) / (w + 30) + hash(i, v, 802)) % 1;
    const dir = hash(i, v, 803) < 0.5 ? 1 : -1;
    const x = d.x - w / 2 - 15 + (dir > 0 ? u : 1 - u) * (w + 30), y = d.y + (i - 1) * 5;
    const lk = LOOKS[(i * 7 + v * 3) % 32];
    const bike = i === 1;
    if (bike) {
      ellipse(c, 'rgba(20,10,6,0.2)', x, y + 1, 8, 1.6);
      ellipse(c, '#2a2e33', x - 5, y - 2.5, 3, 3);
      ellipse(c, '#2a2e33', x + 5, y - 2.5, 3, 3);
      line(c, '#e2493b', 1.1, [x - 5, y - 2.5, x - 1, y - 8, x + 4, y - 8, x + 5, y - 2.5]);
      drawCharacter(c, lk, x, y - 6, { facing: dir > 0 ? 'right' : 'left', phase: (t * 2) % 1, moving: true, blink: false, breath: 0 });
    } else {
      drawCharacter(c, lk, x, y - 1, { facing: dir > 0 ? 'right' : 'left', phase: 0.25, moving: false, blink: false, breath: 0 });
      for (const k of [-2.5, 2.5]) {
        ellipse(c, '#e2493b', x + k - 1.4, y, 1, 1);
        ellipse(c, '#e2493b', x + k + 1.4, y, 1, 1);
      }
    }
  }
}

/** The beach bar (object): a thatched chiringuito with stools, a paella pan and strings of lights at night. */
function chiringuito(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  art(c, `bc:chiri:${night ? 1 : 0}`, x - 44, y - 46, 88, 50, (cc) => {
    shadow(cc, x, y, 40, 3.4, 0.3);
    // Posts, counter, thatched roof.
    for (const px of [x - 34, x + 32]) rect(cc, '#8a6a4a', px, y - 36, 2.4, 36);
    block(cc, x - 36, y - 12, 72, 12, '#a8845a', '#c8a070', '#6a4a30');
    for (let i = 0; i < 12; i++) rect(cc, 'rgba(0,0,0,0.12)', x - 34 + i * 6, y - 11, 0.6, 10);
    poly(cc, '#c9a06a', [x - 44, y - 30, x - 30, y - 46, x + 30, y - 46, x + 44, y - 30]);
    for (let i = 0; i < 28; i++) line(cc, i % 2 ? '#b08a50' : '#d8b478', 1, [x - 42 + i * 3, y - 30, x - 29 + i * 2.1, y - 45]);
    rect(cc, '#8a6a40', x - 44, y - 30.5, 88, 1.2);
    // The sign, bottles, the paella pan with yellow rice, prawns and lemon.
    block(cc, x - 20, y - 36, 40, 7, '#2a6fb0', '#4a90d0', '#1f5590');
    txt(cc, 'XIRINGUITO · PAELLA', x, y - 32.6, 3, '#ffffff', 900);
    for (let i = 0; i < 6; i++) block(cc, x - 32 + i * 4, y - 20, 2.4, 6, ['#2a8a5a', '#e2493b', '#ffd34e', '#7ad0e0', '#f4f1ea', '#8a2a1e'][i], undefined, '#1b1d24');
    ellipse(cc, '#3a3e44', x + 12, y - 14, 11, 4);
    ellipse(cc, '#f5b041', x + 12, y - 14.5, 9.4, 3);
    for (let k = 0; k < 5; k++) ellipse(cc, '#ff6a3a', x + 5 + k * 3.6, y - 15 + (k % 2), 1.4, 0.9);
    ellipse(cc, '#ffd34e', x + 20, y - 15, 1.4, 1.2);
    ellipse(cc, '#57c278', x + 9, y - 16.4, 1.2, 0.8);
    // Stools.
    for (const sx of [x - 24, x - 10, x + 4, x + 20]) {
      block(cc, sx - 3, y + 1, 6, 2.4, '#8a6a4a', '#a8885a');
      rect(cc, '#6a4a30', sx - 2, y + 3, 1, 3);
      rect(cc, '#6a4a30', sx + 1, y + 3, 1, 3);
    }
    if (night) {
      for (let i = 0; i < 12; i++) ellipse(cc, ['#ffd34e', '#ff6a6a', '#7ad0e0'][i % 3], x - 40 + i * 7.2, y - 29 + Math.sin(i) * 1.2, 1.2, 1.2);
      rect(cc, 'rgba(255,214,138,0.22)', x - 36, y - 30, 72, 18);
    }
  });
  for (let i = 0; i < 2; i++) {
    const k = (t * 0.5 + i * 0.5) % 1;
    ellipse(c, `rgba(255,255,255,${0.3 * (1 - k)})`, x + 12 + Math.sin(t * 1.6 + i * 2) * 2, y - 18 - k * 10, 1.5 + k * 2, 1.2 + k * 2);
  }
  person(c, x - 10, y + 4, 16, 'up', 5, t, 3);
}

/** The arrivals hall of El Prat T1: a glass curtain wall under a wavy steel roof, the airport name, flags. */
function terminal(c: Ctx, d: Decor): void {
  const w = d.w ?? 256, h = d.h ?? 96, L = d.x - w / 2, base = d.y, top = base - h, night = isNight();
  art(c, `bc:terminal:${night ? 1 : 0}`, L - 4, top - 24, w + 8, h + 28, (cc) => {
    shadow(cc, d.x, base + 1, w / 2, 4, 0.25, 30);
    // Glass wall with mullions, lit from inside at night.
    const g = cc.createLinearGradient(0, top, 0, base);
    g.addColorStop(0, night ? '#2a3a50' : '#a8d0e8');
    g.addColorStop(1, night ? '#3a4a60' : '#6a9ac0');
    cc.fillStyle = g;
    cc.fillRect(L, top + 12, w, h - 12);
    for (let i = 0; i <= w / 12; i++) rect(cc, night ? '#1b2430' : '#3a4a5a', L + i * 12 - 0.5, top + 12, 1, h - 12);
    for (let k = 1; k < (h - 12) / 16; k++) rect(cc, night ? '#1b2430' : '#3a4a5a', L, top + 12 + k * 16, w, 0.8);
    if (night) for (let i = 0; i < w / 12; i++) for (let k = 0; k < 4; k++) if (hash(i, k, 811) < 0.6) rect(cc, 'rgba(255,230,180,0.35)', L + i * 12 + 1, top + 13 + k * 16, 10, 14);
    rect(cc, 'rgba(255,255,255,0.18)', L, top + 12, w * 0.4, h - 12);
    // Wavy roof.
    cc.fillStyle = night ? '#5a6068' : '#c8ccd0';
    cc.beginPath();
    cc.moveTo(L - 4, top + 12);
    for (let x = L - 4; x <= L + w + 4; x += 8) cc.lineTo(x, top + 4 - Math.sin((x - L) / 40) * 5);
    cc.lineTo(L + w + 4, top + 12);
    cc.closePath();
    cc.fill();
    rect(cc, night ? '#3a4048' : '#8a9096', L - 4, top + 11, w + 8, 2);
    // Name and the flags.
    block(cc, d.x - 70, top + 16, 140, 12, '#1b1d24', '#3a3e44', '#000000');
    txt(cc, 'AEROPORT JOSEP TARRADELLAS BARCELONA–EL PRAT', d.x, top + 22, 3.4, '#ffffff', 900);
    txt(cc, 'T1 · ARRIBADES · LLEGADAS · ARRIVALS', d.x, top + 36, 3.6, night ? '#ffd68a' : '#1b1d24', 900);
    // Inside: the belt, people with trolleys as silhouettes.
    for (let i = 0; i < 10; i++) {
      const px = L + 20 + i * 24;
      rect(cc, night ? 'rgba(255,230,180,0.3)' : 'rgba(30,40,60,0.25)', px - 2, base - 24, 4, 12);
      ellipse(cc, night ? 'rgba(255,230,180,0.3)' : 'rgba(30,40,60,0.25)', px, base - 26, 2.2, 2.2);
    }
    rect(cc, night ? '#1b2430' : '#3a4a5a', L, base - 4, w, 4);
  });
}

/** The arrivals doors (object): sliding glass doors with the pictograms and the "Sortides" arrow. */
function flightDoors(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  const open = (Math.sin(t * 0.8) + 1) / 2;
  block(c, x - 24, y - 40, 48, 40, '#8a9096', '#aab0b6', '#55606a');
  rect(c, night ? '#ffe8b0' : '#d8ecf4', x - 22, y - 38, 44, 36);
  rect(c, '#55606a', x - 11 - open * 9, y - 38, 1.6, 36);
  rect(c, '#55606a', x + 10 + open * 9, y - 38, 1.6, 36);
  rect(c, 'rgba(255,255,255,0.35)', x - 22, y - 38, 10, 36);
  block(c, x - 20, y - 48, 40, 8, '#1b1d24', '#3a3e44', '#000000');
  txt(c, '✈ SORTIDES · SALIDAS', x, y - 44, 3.2, '#ffd34e', 900);
  // Pictograms: plane, suitcase, the "i".
  for (let k = 0; k < 3; k++) {
    block(c, x - 14 + k * 10, y - 34, 8, 8, '#2a6fb0', '#4a90d0');
    txt(c, ['✈', '🧳', 'i'][k], x - 10 + k * 10, y - 30, 4, '#ffffff', 900);
  }
}

/** The gantry sign "Arribades · Llegadas · Arrivals" with the airport pictograms. */
function arrivalsSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 90;
  for (const px of [x - w / 2, x + w / 2 - 2]) rect(c, '#55606a', px, y - 26, 2, 26);
  block(c, x - w / 2 - 2, y - 30, w + 4, 10, '#2a6fb0', '#4a90d0', '#1f5590');
  txt(c, 'Arribades · Llegadas · Arrivals', x, y - 25, 3.6, '#ffffff', 900);
  txt(c, '→ Aerobús · Taxi · Metro L9', x, y - 20.6, 2.2, '#ffd34e', 800);
}

/** The Aerobús: the blue express coach to Plaça Catalunya. */
function aerobus(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 70, night = isNight();
  art(c, `bc:aerobus:${night ? 1 : 0}`, x - w / 2 - 6, y - 30, w + 12, 34, (cc) => {
    shadow(cc, x, y, w / 2 + 2, 4, 0.32);
    for (const wx of [x - w / 2 + 12, x + w / 2 - 12]) {
      ellipse(cc, '#1b1d24', wx, y - 2.5, 4, 3.8);
      ellipse(cc, '#9aa0a6', wx, y - 2.5, 1.6, 1.6);
    }
    block(cc, x - w / 2, y - 26, w, 23, '#2a6fb0', '#4a90d0', '#1f5590');
    rect(cc, '#7ad0e0', x - w / 2 + 1, y - 10, w - 2, 3);
    for (let i = 0; i < Math.floor((w - 12) / 10); i++) rect(cc, night ? '#ffe8b0' : '#b8d8ec', x - w / 2 + 6 + i * 10, y - 22, 8, 9);
    block(cc, x - w / 2 + 2, y - 24, 6, 12, '#2a2e33', '#55606a');
    txt(cc, 'Aerobús', x + 4, y - 15, 4.2, '#ffffff', 900, 'rgba(0,0,0,0.3)');
    txt(cc, 'A1 · Pl. Catalunya', x + 4, y - 6, 2.6, '#ffffff', 800);
  });
}

/** The Aerobús stop (object): a blue totem with the timetable. */
function aerobusStop(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h;
  shadow(c, x, y, 5, 1.8, 0.3, 24);
  block(c, x - 4, y - 30, 8, 30, '#2a6fb0', '#4a90d0', '#1f5590');
  block(c, x - 5, y - 34, 10, 5, '#7ad0e0', '#a8e8f4');
  txt(c, 'A1', x, y - 31.6, 3.4, '#1b1d24', 900);
  for (let k = 0; k < 5; k++) rect(c, '#ffffff', x - 3, y - 26 + k * 3, 6, 1);
  txt(c, 'cada 5 min', x, y - 7, 2, '#ffffff', 800);
}

function taxiSign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  rect(c, '#55606a', x - 0.8, y - 26, 1.6, 26);
  block(c, x - 8, y - 32, 16, 7, '#ffd34e', '#fff0a0', '#c8a020');
  txt(c, 'TAXI', x, y - 28.4, 4, '#1b1d24', 900);
}

/** The L9 metro sign: red diamond with the orange line badge. */
function l9sign(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, night = isNight();
  rect(c, '#55606a', x - 0.8, y - 30, 1.6, 30);
  poly(c, '#e2493b', [x, y - 42, x + 9, y - 33, x, y - 24, x - 9, y - 33]);
  poly(c, '#ffffff', [x, y - 39.4, x + 6.4, y - 33, x, y - 26.6, x - 6.4, y - 33]);
  txt(c, 'M', x, y - 33, 6, '#e2493b', 900);
  block(c, x - 5, y - 22, 10, 5, '#f59e1c', '#ffbb55');
  txt(c, 'L9', x, y - 19.4, 3.2, '#ffffff', 900);
  if (night) ellipse(c, 'rgba(255,80,80,0.25)', x, y - 33, 11, 11);
}

/** Metro entrance (object): the red diamond M sign and a stair rail; `line` badge from the id. */
function metroSign(c: Ctx, r: Rect, lineNo: string, col: string): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  shadow(c, x, y, 5, 1.6, 0.3, 24);
  rect(c, '#55606a', x - 0.8, y - 30, 1.6, 30);
  poly(c, '#e2493b', [x, y - 42, x + 9, y - 33, x, y - 24, x - 9, y - 33]);
  poly(c, '#ffffff', [x, y - 39.4, x + 6.4, y - 33, x, y - 26.6, x - 6.4, y - 33]);
  txt(c, 'M', x, y - 33, 6, '#e2493b', 900);
  block(c, x - 5, y - 22, 10, 5, col, shade(col, 0.3));
  txt(c, lineNo, x, y - 19.4, 3.2, '#ffffff', 900);
  // Stairs down.
  block(c, x - 7, y - 6, 14, 6, '#4a4e54', '#6a6e74', '#2a2e33');
  for (let k = 0; k < 3; k++) rect(c, `rgba(0,0,0,${0.2 + k * 0.2})`, x - 6, y - 5 + k * 1.6, 12, 1.2);
  if (night) ellipse(c, 'rgba(255,80,80,0.25)', x, y - 33, 11, 11);
}

/** Luggage trolleys in a row. */
function trolleys(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, w = d.w ?? 30;
  for (let i = 0; i < 3; i++) {
    const tx = x - w / 2 + i * (w / 3) + 4;
    ellipse(c, '#2a2e33', tx - 3, y - 1, 1.4, 1.4);
    ellipse(c, '#2a2e33', tx + 3, y - 1, 1.4, 1.4);
    rect(c, '#9aa0a6', tx - 4, y - 4, 8, 1.2);
    rect(c, '#9aa0a6', tx + 3.5, y - 14, 1, 10);
    rect(c, '#9aa0a6', tx + 2, y - 14, 4, 1);
    if (i !== 1) block(c, tx - 3.5, y - 10, 6, 6, i ? '#e2493b' : '#2a6fb0', undefined, '#1b1d24');
  }
}

/** A jet climbing out over the airport, with a contrail. */
function jet(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 180;
  const u = (t * 0.03) % 1;
  const x = d.x - w / 2 + u * (w + 60), y = d.y - 40 - u * 30;
  c.strokeStyle = 'rgba(255,255,255,0.5)';
  c.lineWidth = 1.2;
  c.beginPath();
  c.moveTo(x - 10, y + 1);
  c.lineTo(x - 50, y + 10);
  c.stroke();
  poly(c, '#f4f1ea', [x - 10, y, x + 10, y, x + 14, y + 1.4, x + 10, y + 2.8, x - 10, y + 2.8]);
  poly(c, '#e2493b', [x - 10, y, x - 7, y - 6, x - 4, y - 6, x - 4, y]);
  poly(c, '#c8cdd2', [x - 2, y + 1.4, x + 4, y + 1.4, x + 1, y + 5]);
  poly(c, '#c8cdd2', [x - 2, y + 1.4, x + 4, y + 1.4, x + 2, y - 2]);
}

/** The flags in front of the terminal: EU, Spain, Catalonia, Barcelona. */
function flags(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  for (let i = 0; i < 4; i++) {
    const fx = x + i * 10;
    rect(c, '#9aa0a6', fx - 0.5, y - 34, 1, 34);
    const wv = Math.sin(t * 3 + i) * 1.2;
    if (i === 0) {
      poly(c, '#2a4fa0', [fx, y - 34, fx + 9, y - 34 + wv, fx + 9, y - 28 + wv, fx, y - 28]);
      for (let k = 0; k < 6; k++) ellipse(c, '#ffd34e', fx + 4.5 + Math.cos((k / 6) * Math.PI * 2) * 2.2, y - 31 + Math.sin((k / 6) * Math.PI * 2) * 2, 0.4, 0.4);
    } else if (i === 1) {
      poly(c, '#e2493b', [fx, y - 34, fx + 9, y - 34 + wv, fx + 9, y - 28 + wv, fx, y - 28]);
      poly(c, '#ffd34e', [fx, y - 32.5, fx + 9, y - 32.5 + wv, fx + 9, y - 29.5 + wv, fx, y - 29.5]);
    } else if (i === 2) senyera(c, fx, y - 34, 9, 6, t);
    else {
      poly(c, '#f4f1ea', [fx, y - 34, fx + 9, y - 34 + wv, fx + 9, y - 28 + wv, fx, y - 28]);
      rect(c, '#e2493b', fx + 4.5, y - 34, 4.5, 3);
      rect(c, '#e2493b', fx, y - 31, 4.5, 3);
      rect(c, '#ffd34e', fx + 4.5, y - 31, 4.5, 3);
      rect(c, '#ffd34e', fx, y - 34, 4.5, 3);
    }
  }
}

function benchAirport(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 30;
  shadow(c, x, y, w / 2, 2, 0.2);
  block(c, x - w / 2, y - 9, w, 3, '#55606a', '#7a8086', '#3a3e44');
  for (let i = 0; i < 2; i++) person(c, x - 7 + i * 14, y - 6, 15 + i * 9, 'down', i ? 4 : 3, t, i);
  block(c, x - w / 2, y - 6, w, 3, '#55606a', '#7a8086', '#3a3e44');
}

/** The pond rail (variant 1: the far side, drawn lower so the water shows in front). */
function pondRail(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  rect(c, '#3a3e44', L, y - 6, w, 0.8);
  rect(c, '#3a3e44', L, y - 3, w, 0.6);
  for (let i = 0; i <= w; i += 8) rect(c, '#3a3e44', L + i, y - 6, 0.8, 6);
}

/** The Sagrada's towers mirrored in the pond, rippling. */
function reflection(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 160, h = d.h ?? 32, L = d.x - w / 2, top = d.y - h;
  c.save();
  c.globalAlpha = 0.35;
  for (let k = 0; k < 4; k++) {
    const tx = d.x + (k - 1.5) * w * 0.22 + Math.sin(t * 1.5 + k) * 1;
    poly(c, '#6a5a48', [tx - 5, top, tx + 5, top, tx + 2, top + h - 2, tx - 2, top + h - 2]);
  }
  poly(c, '#7a6a58', [d.x - 20, top, d.x + 20, top, d.x + 12, top + h * 0.5, d.x - 12, top + h * 0.5]);
  c.globalAlpha = 1;
  for (let i = 0; i < 5; i++) {
    const u = (t * 0.3 + i / 5) % 1;
    ellipse(c, `rgba(255,255,255,${0.25 * (1 - u)})`, L + 20 + hash(i, 1, 821) * (w - 40), top + 6 + hash(i, 2, 821) * (h - 12), 4 + u * 10, 1 + u * 2.5);
  }
  c.restore();
}

/** A souvenir stall at the Sagrada: postcards, fridge magnets, little Gaudí lizards. */
function souvenir(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, night = isNight();
  art(c, `bc:souv:${night ? 1 : 0}`, x - 22, y - 36, 44, 40, (cc) => {
    shadow(cc, x, y, 18, 3, 0.3);
    block(cc, x - 16, y - 10, 32, 10, '#8a6a4a', '#a8885a', '#5a4a30');
    for (const px of [x - 15, x + 13]) rect(cc, '#3a2a20', px, y - 30, 2, 20);
    poly(cc, '#e2493b', [x - 20, y - 28, x - 14, y - 34, x + 14, y - 34, x + 20, y - 28]);
    for (let i = 0; i < 6; i++) if (i % 2) poly(cc, '#fff6e6', [x - 20 + i * 6.6, y - 28, x - 20 + i * 6.6 + 6.6, y - 28, x - 14 + i * 4.7 + 4.7, y - 34, x - 14 + i * 4.7, y - 34]);
    for (let j = 0; j < 2; j++) for (let i = 0; i < 6; i++) rect(cc, TRENCADIS[(i + j * 3) % 8], x - 14 + i * 4.8, y - 24 + j * 6, 4, 5);
    for (let i = 0; i < 4; i++) ellipse(cc, '#4fae4a', x - 12 + i * 8, y - 11, 2.4, 1.4);
    if (night) rect(cc, 'rgba(255,214,138,0.22)', x - 16, y - 28, 32, 18);
  });
  person(c, x, y - 12, 23, 'down', 0, t, 5);
}

/** The Plaça Catalunya fountain: a stone bowl with jets. */
function fountainCat(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, w = d.w ?? 40;
  shadow(c, x, y, w / 2, 3, 0.25);
  ellipse(c, '#a8a49c', x, y - 3, w / 2, 7);
  ellipse(c, '#5aa8c8', x, y - 3.5, w / 2 - 3, 5.4);
  ellipse(c, '#8ac8e0', x - 4, y - 5, w / 4, 2);
  block(c, x - 3, y - 20, 6, 14, '#a8a49c', '#c8c4ba', '#6a6660');
  ellipse(c, '#a8a49c', x, y - 20, 7, 2.4);
  for (let k = 0; k < 5; k++) {
    const u = (t * 1.3 + k / 5) % 1;
    const a = (k / 5) * Math.PI * 2;
    ellipse(c, `rgba(220,240,255,${0.7 * (1 - u)})`, x + Math.cos(a) * u * 10, y - 22 - Math.sin(u * Math.PI) * 10 + u * 16, 1.2, 1.2);
  }
}

/** An old man feeding the pigeons from a paper bag. */
function pigeonMan(c: Ctx, d: Decor, t: number): void {
  person(c, d.x, d.y, 3, 'down', 0, t);
  rect(c, '#c8a060', d.x + 6, d.y - 12, 3, 4);
  for (let i = 0; i < 4; i++) {
    const u = (t * 0.8 + i / 4) % 1;
    ellipse(c, `rgba(240,220,160,${1 - u})`, d.x + 8 + u * 6 + i, d.y - 10 + u * 8, 0.6, 0.6);
  }
}

/** The Plaça Reial fountain of the Three Graces. */
function fountainReial(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 14, 3, 0.25);
  ellipse(c, '#a8a49c', x, y - 2, 13, 5);
  ellipse(c, '#5aa8c8', x, y - 2.5, 11, 3.8);
  block(c, x - 2.5, y - 20, 5, 16, '#a8a49c', '#c8c4ba', '#6a6660');
  for (let k = 0; k < 3; k++) {
    const a = (k / 3) * Math.PI * 2;
    ellipse(c, '#c8c4ba', x + Math.cos(a) * 3, y - 24 + Math.sin(a) * 1, 1.6, 3.2);
  }
  for (let k = 0; k < 4; k++) {
    const u = (t * 1.2 + k / 4) % 1;
    ellipse(c, `rgba(220,240,255,${0.6 * (1 - u)})`, x + (k - 1.5) * 4 + u * (k - 1.5) * 3, y - 18 + u * 14, 1, 1);
  }
}

/** A plane tree (plátano): mottled grey-green trunk, a broad crown, as along La Rambla. */
function planeTree(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = (d.variant ?? 0) % 3;
  const sway = wind.at(x, y) * 1.5 + Math.sin(t * 0.8 + x * 0.1) * 0.4;
  shadow(c, x, y, 15, 4, 0.3, 30);
  poly(c, '#b0a890', [x - 3.2, y, x - 2.4, y - 26, x + 2.4, y - 26, x + 3.4, y]);
  for (let k = 0; k < 8; k++) ellipse(c, k % 2 ? '#8a9a7a' : '#d8d0b8', x - 1.4 + hash(k, v, 831) * 3, y - 4 - hash(k, v, 832) * 20, 1.2, 1.8);
  const cx = x + sway, cy = y - 38;
  const dark = '#3f7a3a', mid = '#5a9a4a', light = '#8ac060';
  for (const [dx, dy, r] of [[-12, 2, 10], [12, 1, 10], [0, -8, 11], [-6, -4, 9], [7, -4, 9], [0, 3, 10]] as const) ellipse(c, dark, cx + dx, cy + dy, r, r * 0.8);
  for (const [dx, dy, r] of [[-9, 0, 7], [9, -1, 7], [0, -9, 8], [-4, -5, 6], [5, -5, 6]] as const) ellipse(c, mid, cx + dx - 1, cy + dy - 1.5, r, r * 0.8);
  for (const [dx, dy, r] of [[-8, -3, 3.5], [1, -11, 4], [7, -5, 3]] as const) ellipse(c, light, cx + dx - 1.5, cy + dy - 1.5, r, r * 0.8);
  if (v === 2) for (let k = 0; k < 3; k++) ellipse(c, '#8a6a3a', cx + (k - 1) * 8, cy + 8, 1.1, 1.1);
}

// ---------------------------------------------------------------------------
// City services: the rental kiosk, the toilet cabin, the cash machine
// ---------------------------------------------------------------------------

function rentShop(c: Ctx, r: Rect, t: number): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  shadow(c, x, y, 24, 3, 0.3);
  block(c, x - 20, y - 24, 40, 24, '#2a8a5a', '#4aa878', '#1a5a3a');
  block(c, x - 22, y - 30, 44, 7, '#ffd34e', '#fff0a0', '#c8a020');
  txt(c, 'LLOGUER · RENT A BIKE', x, y - 26.6, 3, '#1b1d24', 900);
  rect(c, night ? '#ffe8b0' : '#8fb8d8', x - 17, y - 21, 20, 10);
  rect(c, '#2a2420', x + 7, y - 20, 10, 20);
  // A bike and a kick scooter leaning in front.
  ellipse(c, '#2a2e33', x - 16, y - 3, 3, 3);
  ellipse(c, '#2a2e33', x - 8, y - 3, 3, 3);
  line(c, '#e2493b', 1.2, [x - 16, y - 3, x - 13, y - 9, x - 9, y - 9, x - 8, y - 3]);
  rect(c, '#1b1d24', x - 1, y - 14, 1, 12);
  ellipse(c, '#1b1d24', x - 1.5, y - 1.5, 1.6, 1.6);
  ellipse(c, '#1b1d24', x + 3, y - 1.5, 1.6, 1.6);
  rect(c, '#1b1d24', x - 1.5, y - 2.5, 5, 1);
  person(c, x + 12, y + 2, 17, 'up', 0, t);
}

function toilet(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  shadow(c, x, y, 14, 2.6, 0.3);
  block(c, x - 12, y - 26, 24, 26, '#8a9096', '#aab0b6', '#55606a');
  block(c, x - 13, y - 30, 26, 5, '#4a4e54', '#6a6e74', '#2a2e33');
  rect(c, '#2a2420', x - 9, y - 20, 7, 20);
  rect(c, '#2a2420', x + 2, y - 20, 7, 20);
  txt(c, '🚻', x, y - 23, 5, '#ffffff', 900);
  txt(c, 'WC', x, y - 27.6, 3, '#ffffff', 900);
  if (night) rect(c, 'rgba(255,230,180,0.3)', x - 11, y - 25, 22, 4);
}

function atm(c: Ctx, r: Rect): void {
  const x = r.x + r.w / 2, y = r.y + r.h, night = isNight();
  shadow(c, x, y, 10, 2, 0.3);
  block(c, x - 9, y - 30, 18, 30, '#2a3a6e', '#3a5aa0', '#1a2440');
  block(c, x - 7, y - 24, 14, 9, night ? '#9fd8ff' : '#7ac0e8', '#c8ecff');
  txt(c, '€', x, y - 19.6, 5, '#1b1d24', 900);
  rect(c, '#1b1d24', x - 5, y - 12, 10, 1.4);
  for (let k = 0; k < 3; k++) rect(c, '#c8cdd2', x - 4 + k * 3, y - 8, 2, 2);
  txt(c, 'CAIXER', x, y - 27.5, 2.4, '#ffd34e', 900);
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

const OBJECTS: Record<string, (c: Ctx, r: Rect, t: number) => void> = {
  bc_flight: flightDoors,
  bc_aerobus: aerobusStop,
  bc_metroGuell: (c, r) => metroSign(c, r, 'L3', '#2f8a5a'),
  bc_metroSagrada: (c, r) => metroSign(c, r, 'L5', '#2a6fb0'),
  bc_metroLiceu: (c, r) => metroSign(c, r, 'L3', '#2f8a5a'),
  bc_metroBeach: (c, r) => metroSign(c, r, 'L4', '#e8b020'),
  bc_guellBench: guellBench,
  bc_dragon: dragon,
  bc_sagrada: (c, r) => {
    // The ticket gate at the foot of the Nativity facade (the building itself is bc_sagrada decor).
    const x = r.x + r.w / 2, y = r.y + r.h;
    block(c, x - 18, y - 6, 36, 6, '#8a9096', '#aab0b6', '#55606a');
    for (let k = -1; k <= 1; k++) rect(c, '#2a2e33', x + k * 10 - 1, y - 14, 2, 8);
    block(c, x - 14, y - 20, 28, 6, '#2a6fb0', '#4a90d0', '#1f5590');
    txt(c, 'ENTRADA · TICKETS', x, y - 17, 2.8, '#ffffff', 900);
  },
  bc_canaletes: canaletes,
  bc_flowers: flowerKiosk,
  bc_statue: humanStatue,
  bc_boqueria: boqueriaStall,
  bc_columbus: columbus,
  bc_cathedral: (c, r) => {
    const x = r.x + r.w / 2, y = r.y + r.h;
    block(c, x - 14, y - 4, 28, 4, '#a8a49c', '#c8c4ba', '#6a6660');
    rect(c, '#2a2420', x - 9, y - 16, 18, 12);
    poly(c, '#a8a49c', [x - 10, y - 14, x, y - 22, x + 10, y - 14, x + 9, y - 16, x, y - 20, x - 9, y - 16]);
  },
  bc_xurros: xurreria,
  bc_castellers: castell,
  bc_petanca: petancaBox,
  bc_chiringuito: chiringuito,
  rent_bc: rentShop,
  wc_bc: toilet,
  bank_atmBc: atm,
};

function drawDecor(c: Ctx, d: Decor, t: number): boolean {
  if (!d.kind.startsWith('bc_')) return false;
  const kind = d.kind.slice(3);
  if (kind === 'vsign') {
    vsign(c, d);
    return true;
  }
  if (drawLife(c, kind, d, t)) return true;
  switch (kind) {
    case 'facade': facade(c, d, t); return true;
    case 'sagrada': sagrada(c, d); return true;
    case 'crane': crane(c, d, t); return true;
    case 'cathedral': cathedral(c, d); return true;
    case 'wHotel': wHotel(c, d); return true;
    case 'arc': arc(c, d); return true;
    case 'cascada': cascada(c, d, t); return true;
    case 'gatehouse': gatehouse(c, d); return true;
    case 'guellGate': guellGate(c, d); return true;
    case 'guellWall': guellWall(c, d); return true;
    case 'columns': columns(c, d); return true;
    case 'terraceEdge': terraceEdge(c, d); return true;
    case 'guellSign': guellSign(c, d); return true;
    case 'ironwork': ironwork(c, d); return true;
    case 'lookout': lookout(c, d, t); return true;
    case 'parrots': parrots(c, d, t); return true;
    case 'agave': agave(c, d); return true;
    case 'lamp': lamp(c, d); return true;
    case 'falques': falques(c, d, t); return true;
    case 'gaudiLamp': gaudiLamp(c, d); return true;
    case 'bus': bus(c, d); return true;
    case 'taxi': taxi(c, d); return true;
    case 'vespa': vespa(c, d); return true;
    case 'bicing': bicing(c, d); return true;
    case 'kiosk': kiosk(c, d); return true;
    case 'senyera': senyeraDecor(c, d, t); return true;
    case 'pharmacy': pharmacy(c, d, t); return true;
    case 'awning': awning(c, d, t); return true;
    case 'busStop': busStop(c, d, t); return true;
    case 'terrace': terrace(c, d, t); return true;
    case 'painter': painter(c, d, t); return true;
    case 'miro': miro(c, d); return true;
    case 'trashBin': trashBin(c, d); return true;
    case 'quay': quay(c, d); return true;
    case 'yacht': yacht(c, d, t); return true;
    case 'golondrina': golondrina(c, d, t); return true;
    case 'deckRail': deckRail(c, d); return true;
    case 'gulls': gulls(c, d, t); return true;
    case 'boqueria': boqueria(c, d); return true;
    case 'liceu': liceu(c, d); return true;
    case 'generalitat': generalitat(c, d, t); return true;
    case 'bishopBridge': bishopBridge(c, d); return true;
    case 'sardana': sardana(c, d, t); return true;
    case 'geese': geese(c, d, t); return true;
    case 'castellCrowd': castellCrowd(c, d, t); return true;
    case 'petancaCourt': petancaCourt(c, d, t); return true;
    case 'boats': boats(c, d, t); return true;
    case 'mammoth': mammoth(c, d); return true;
    case 'picnic': picnic(c, d, t); return true;
    case 'promRail': promRail(c, d); return true;
    case 'shower': shower(c, d, t); return true;
    case 'sunbathers': sunbathers(c, d, t); return true;
    case 'sandcastle': sandcastle(c, d); return true;
    case 'surf': surf(c, d, t); return true;
    case 'skaters': skaters(c, d, t); return true;
    case 'terminal': terminal(c, d); return true;
    case 'arrivalsSign': arrivalsSign(c, d); return true;
    case 'aerobus': aerobus(c, d); return true;
    case 'taxiSign': taxiSign(c, d); return true;
    case 'l9sign': l9sign(c, d); return true;
    case 'trolleys': trolleys(c, d); return true;
    case 'jet': jet(c, d, t); return true;
    case 'flags': flags(c, d, t); return true;
    case 'benchAirport': benchAirport(c, d, t); return true;
    case 'pondRail': pondRail(c, d); return true;
    case 'reflection': reflection(c, d, t); return true;
    case 'souvenir': souvenir(c, d, t); return true;
    case 'fountainCat': fountainCat(c, d, t); return true;
    case 'pigeonMan': pigeonMan(c, d, t); return true;
    case 'fountainReial': fountainReial(c, d, t); return true;
    case 'plane': planeTree(c, d, t); return true;
    case 'solid': return true;
    default: return false;
  }
}

registerPropDrawers({
  objects: OBJECTS,
  decor: drawDecor,
  animatedObjects: ['bc_flight', 'bc_guellBench', 'bc_dragon', 'bc_flowers', 'bc_statue', 'bc_canaletes', 'bc_boqueria', 'bc_xurros', 'bc_castellers', 'bc_chiringuito', 'rent_bc'],
  animatedDecor: [
    'bc_facade', 'bc_crane', 'bc_cascada', 'bc_lookout', 'bc_parrots', 'bc_falques', 'bc_senyera', 'bc_pharmacy', 'bc_awning', 'bc_busStop', 'bc_terrace', 'bc_painter',
    'bc_yacht', 'bc_golondrina', 'bc_gulls', 'bc_generalitat', 'bc_sardana', 'bc_geese', 'bc_castellCrowd', 'bc_petancaCourt', 'bc_boats', 'bc_picnic', 'bc_shower', 'bc_sunbathers',
    'bc_surf', 'bc_skaters', 'bc_jet', 'bc_flags', 'bc_benchAirport', 'bc_reflection', 'bc_souvenir', 'bc_fountainCat', 'bc_pigeonMan', 'bc_fountainReial', 'bc_plane',
    ...LIFE_ANIMATED.map((k) => `bc_${k}`),
  ],
});
